<template>
  <div style="width:100%;" class="mt-4 gl-accounts-container">
    <div class="grid-controls">
      <div class="grid-radio-controls">
        <res-radio-group label="Select View"
                         labelColor="gray"
                         :value="selectedViewOption"
                         :options="viewOptions"
                         @input="selectedViewOptionChanged">
        </res-radio-group>
      </div>
      <div class="grid-radio-controls" v-show="selectedViewOption == 0">
        <res-radio-group label="Historicals"
                         labelColor="gray"
                         :value="historicalYearsDisplayCount"
                         :readonly="selectedViewOption == 1"
                         :options="historicalRadioOptions"
                         @input="historicalDataYearsDisplayedChanged">
        </res-radio-group>
      </div>
      <div class="add-columns-controls">
        <span class="add-columns-label">Add Columns</span>
        <v-checkbox v-show="selectedViewOption == 1"
                    class="add-columns-option"
                    color="var(--v-resBlue-darken1)"
                    label="Unit Details"
                    v-model="showUnitDetails"
                    @change="toggleUnitDetailsColumn">
          <template v-slot:label>
            <div class="resGrey--text text--darken-1">
              Unit Details
            </div>
          </template>
        </v-checkbox>
        <v-checkbox v-show="selectedViewOption == 1"
                    class="add-columns-option"
                    color="var(--v-resBlue-darken1)"
                    label="Sq Ft Details"
                    v-model="showSqftDetails"
                    @change="toggleSqftDetailsColumn">
          <template v-slot:label>
            <div class="resGrey--text text--darken-1">
              Sq Ft Details
            </div>
          </template>
        </v-checkbox>
        <v-checkbox class="add-columns-option"
                    color="var(--v-resBlue-darken1)"
                    label="Calculations"
                    v-model="showCalculations"
                    @change="toggleCalculationsColumn">
          <template v-slot:label>
            <div class="resGrey--text text--darken-1">
              Calculations
            </div>
          </template>
        </v-checkbox>
        <v-checkbox class="add-columns-option"
                    color="var(--v-resBlue-darken1)"
                    label="Assumptions"
                    v-model="showAssumptions"
                    @change="toggleAssumptionsColumn">
          <template v-slot:label>
            <div class="resGrey--text text--darken-1">
              Assumptions
            </div>
          </template>
        </v-checkbox>
      </div>
      <div class="help-controls" @click="openHelp(null)">
        <span class="help-label">Help</span>
        <v-icon color="var(--v-resBlue-darken1)" class="pb-1" small>mdi-help-circle</v-icon>
      </div>
    </div>

    <v-progress-linear indeterminate v-if="currentLoading"></v-progress-linear>

    <!-- Detailed -->
    <div class="gl-view-container">
      <!-- Group Totals -->
      <div class="gl-totals-view">
        <ag-grid-vue v-show="selectedViewOption == 0"
                    :style="{width: '100%', height: 'fit-content' }"
                    class="ag-theme-material group-totals-table"
                    :gridOptions="groupDataGridOptions"
                    :columnDefs="currentHeaders"
                    :rowData="groupData"
                    :isExternalFilterPresent="shouldRowsBeFiltered"
                    :doesExternalFilterPass="shouldShowRow">
        </ag-grid-vue>
        <span v-if="historicalYearsDisplayCount > 0 && estimatedFutureHistoricalBasis && selectedViewOption == 0" class="future-historical-indicator mb-5">*Indicates Estimated Future Historicals are based on {{estimatedFutureHistoricalBasis}}</span>

        <!-- Select All -->
        <res-select-update-header v-show="selectedViewOption == 0"
                                  :showCalculator="true"
                                  :minWidth="900"
                                  :allCheckSelected="allCheckSelected"
                                  :anyCheckSelected="anyCheckSelected"
                                  @selectionChanged="selectAll"
                                  @bulkCalculatorOpened="openBulkCalculator"
                                  :hideUpdateBtn="true">
        </res-select-update-header>
      </div>

      <!-- GL Accounts -->
      <div class="gl-details-view">
        <ag-grid-vue v-show="selectedViewOption == 0"
                    style="width: 100%; height:auto;"
                    class="ag-theme-material gl-detail-grid"
                    :gridOptions="glAccountDataGridOptions"
                    :columnDefs="currentHeaders"
                    :rowData="pagedGLAccountData"
                    :headerHeight="0"
                    :isFullWidthCell="isHeaderCell"
                    :isExternalFilterPresent="shouldRowsBeFiltered"
                    :doesExternalFilterPass="shouldShowRow"
                    @cell-value-changed="onCellValuesChanged"
                    @cell-editing-started="cellEditingStarted"
                    @cell-editing-stopped="cellEditingStopped"
                    @cell-clicked="onCellClicked">
        </ag-grid-vue>
      </div>
    </div>

    <!-- Summary -->
    <div>
      <ag-grid-vue v-show="selectedViewOption == 1"
                   style="height: 100%; width: 100%;"
                   class="ag-theme-material gl-summary-grid mb-5"
                   :gridOptions="groupSummaryDataGridOptions"
                   :columnDefs="summaryHeaders"
                   :rowData="groupSummaryData"
                   :doesExternalFilterPass="shouldRowsBeFiltered"
                   :isExternalFilterPresent="shouldRowsBeFiltered">
      </ag-grid-vue>
    </div>

    <!-- Pagination -->
    <v-container class="align-center mt-5 paginator" v-show="!currentLoading && glAccountData.length > 1 && glPagesMax > 0">
      <v-row>
        <v-col cols="4">
          <v-pagination v-model="currentPage"
                        color="resGrey darken-1"
                        type="number"
                        @input="setGLPageSize"
                        :length="glPagesMax"
                        class="pl-3">
          </v-pagination>
        </v-col>
        <v-spacer></v-spacer>
        <res-text-field class="pagination-input mt-4" @input="setGLPageSize" type="number" v-model="pageSize"></res-text-field>
        <label class="pr-4 pl-2 mt-5 pagination-rows-display">GL {{ pageMin + 1 }} - {{ pageMax }} ({{ glAccountsTotal }} Total)</label>
      </v-row>
    </v-container>

      <!-- Dialogs -->
      <res-dialog :showDialog="showHelp"
                  titleClass="resBlue darken-1 white--text"
                  iconColor="white"
                  cancelButtonText="Close"
                  :cancelButtonOnly="true"
                  cancelButtonColor="resBlue darken-1"
                  :maxWidth="800"
                  :setWidth="800"
                  :setHeight="600"
                  attach=".gl-accounts-container"
                  @cancel="closeHelp">
        <budget-general-ledger-help :openSection="openHelpSection"></budget-general-ledger-help>
      </res-dialog>

      <res-dialog :showDialog="showNotEditable"
                  saveButtonText="Okay"
                  :saveButtonOnly="true"
                  :hideClose="true"
                  @save="closeNotEditable">
        {{notEditableReason}}
      </res-dialog>

      <clear-all-dialog v-if="showClearResetDialog"
                        :showDialog="showClearResetDialog"
                        altText="This will clear all data from this GL Account's budgeted amounts and add back any starting default numbers. Are you sure you want to do this?"
                        :maxWidth="700"
                        :setWidth="700"
                        @confirm="clearResetConfirmed"
                        @cancel="closeClearResetDialog">
      </clear-all-dialog>

      <comment-history :commentService="commentService"
                       :showDialog="isCommentsDialogOpen"
                       :readonly="readonly"
                       @flagged="updateIsFlagged"
                       @activity="updateCommentActivity"
                       @close="closeCommentsDialog">
      </comment-history>

      <select-multiple-dialog v-if="isDeleteRowsDialogOpen"
                              :showDialog="isDeleteRowsDialogOpen"
                              title="Delete Working Rows"
                              message="Please use the checkbox to select the row(s) you want to delete, then press the DELETE button."
                              allSelectedConfirmationMessage="Removing all working rows for this GL Account will cause the BUDGET row to be editable. Any numbers currently on this GL Account BUDGET row will be cleared. Do you want to continue? Cancel or Yes."
                              buttonText="DELETE"
                              :options="deletingGLAccountRowsOptions"
                              @confirm="confirmDeleteWorkingRows"
                              @cancel="hideDeleteRowsDialog">
      </select-multiple-dialog>

      <res-dialog :showDialog="isAddWorkingRowsDialogOpen"
                  title="Add Working Rows"
                  saveButtonText="Yes"
                  cancelButtonText="Cancel"
                  @save="confirmAddWorkingRowsDialog"
                  @cancel="closeAddWorkingRowsDialog">
        Adding working rows to this GL Account will cause the BUDGET row to become a sum of the working row(s) which can not be edited. Any numbers currently on this GL Account BUDGET row will be cleared. Do you want to continue? Cancel or Yes.
      </res-dialog>

      <create-calculation-dialog :showDialog="isCreateCalculationDialogOpen"
                                 :budgetIterationId="budgetIterationId"
                                 :fiscalYearStartMonth="fiscalYearStartMonth"
                                 :accountingBasisId="accountingBasisId"
                                 :objects="selectedGLAccounts"
                                 :maxWidth="1200"
                                 @save="loadBudgetGLAccounts"
                                 @cancel="closeCreateCalculationDialog">
      </create-calculation-dialog>

      <assumptions-dialog :showDialog="isAssumptionsDialogOpen"
                          :calculations="assumptionCalculations"
                          :assumptionStatement="assumptionStatement"
                          :maxWidth="1000"
                          :readonly="readonly"
                          @save="updateAssumptionStatement"
                          @cancel="closeAssumptionsDialog">
      </assumptions-dialog>
  </div>
</template>

<script>
  import YearGrid from '@/mixins/YearGrid';
  import { AgGridVue } from "ag-grid-vue";
  import ResSelectUpdateHeader from '@/components/base/ResSelectUpdateHeader';
  import ResActionGroupHeader from '@/components/base/ResActionGroupHeader';
  import ResRadioGroup from "@/components/base/ResRadioGroup";
  import GLTitleCell from "@/components/budgets/general-ledger/GLTitleCell.vue"
  import LinkableCell from "@/components/budgets/general-ledger/LinkableCell.vue"
  import GLNameNumberCell from "@/components/budgets/general-ledger/GLNameNumberCell.vue"
  import ClearAllDialog from "@/components/budgets/dialogs/ClearAllDialog.vue"
  import CommentHistory from '@/components/budgets/dialogs/CommentHistory.vue'
  import SelectMultipleDialog from '@/components/base/dialogs/MultiSelectDialog.vue'
  import ResDialog from '@/components/base/ResDialog.vue';
  import CreateCalculationDialog from '@/components/budgets/general-ledger/dialogs/CreateCalculationDialog.vue'
  import AssumptionsDialog from '@/components/budgets/general-ledger/dialogs/AssumptionsDialog.vue'
  import ResTextField from '@/components/base/ResTextField.vue';

  import BudgetGeneralLedgerHelp from '@/components/help/BudgetGeneralLedgerHelp.vue';
  import HelpableMixin from '@/mixins/Helpable.js';
  import EditBudget from '@/mixins/EditBudget.js';

  import UserService from '@/services/users';
  import budgetService from '@/services/budget';
  import budgetGLAccountService from '@/services/budget-gl-account';
  import _debounce from 'lodash/debounce';
  import { mapActions, mapGetters } from 'vuex';

  export default {
    name: 'GroupGLAccountBudgets',
    mixins: [YearGrid, HelpableMixin, EditBudget],
    components: {
      AgGridVue,
      ResSelectUpdateHeader,
      ClearAllDialog,
      ResRadioGroup,
      ResDialog,
      CommentHistory,
      SelectMultipleDialog,
      AssumptionsDialog,
      CreateCalculationDialog,
      BudgetGeneralLedgerHelp,
      /* eslint-disable vue/no-unused-components */
      ResActionGroupHeader, // used by AG Grid
      ResTextField, // pagination
      GLNameNumberCell, // used by AG Grid
      GLTitleCell, // used by AG Grid
      LinkableCell // used by AG Grid
      /* eslint-enable vue/no-unused-components */
    },
    props: {
      budgetIterationId: {
        required: true
      },
      groupId: {
        required: true
      }
    },
    data: function () {
      return {
        showTotals: true,
        showUnitDetails: true,
        showSqftDetails: false,
        showPriorYrBudgetDetails: false,
        showCurrentYrBudgetDetails: false,
        showPriorYrReforecastDetails: false,
        showCalculations: false,
        showAssumptions: false,
        fiscalYearStartMonth: 1,
        fiscalYear: null,
        accountingBasisId: 1,
        showClearResetDialog: false,
        availableColumns: null,
        summaryColumns: null,
        budgetIterationLastModifiedTimestamp: null,
        summaryRefreshIsQueued: false,
        historicalRadioOptions: [
          { label: 'None', value: 0 },
          { label: '1-Year', value: 1 },
          { label: '2-Year', value: 2 }
        ],
        historicalYearsDisplayCount: 0, // default to zero year of history
        viewOptions: [
          { label: 'Detailed', value: 0 },
          { label: 'Summary', value: 1 }
        ],
        selectedViewOption: 0,// default to detailed view
        groupData: [],
        groupDataGridOptions: {
          headerHeight: 36,
          rowHeight: 36,
          columnDefs: this.availableColumns,
          alignedGrids: [],
          domLayout: 'autoHeight',
          suppressHorizontalScroll: true,
          rowClassRules: {
            'historic-cell': () => true,
            'gl-title': () => true
          }
        },
        glAccountData: [],
        pagedGLAccountData: [],
          glAccountDataGridOptions: {
          getRowHeight: params => params.data.isHeader ? 72 : null,
          domLayout: 'autoHeight',
          columnDefs: this.availableColumns,
          fullWidthCellRendererFramework: 'ResActionGroupHeader',
          rowClassRules: {
            'historic-cell': params => Boolean(params.data.isHistorical),
            'gl-title': () => true,
            'readonly-row-dark': params => Boolean(!params.data.isEditable)
          },
          alignedGrids: [],
          singleClickEdit: true,
          stopEditingWhenGridLosesFocus: true
        },
        groupSummaryData: [],
        groupSummaryDataGridOptions: {
          columnDefs: this.summaryColumns,
          headerHeight: 72 / 2 * 3,
          groupHeaderHeight: 72 / 2,
          domLayout: 'autoHeight',
          alignedGrids: [],
          rowClassRules: {
            'historic-cell': () => true,
            'summary-gl-title': (params) => Boolean(params.data.isSummary),
            'gl-data-cell': () => true
          },
        },
        allCheckSelected: false,
        anyCheckSelected: false,
        gridLeftActions: [
          {
            name: 'toggle-gl-locked',
            icon: (params) => params.isLocked ? 'mdi-lock-outline' : 'mdi-lock-open-outline',
            class: (params) => params.isLocked ? 'locked' : 'unlocked',
            label: (params) => params.isLocked ? this.titleIconHoverTexts.locked : this.titleIconHoverTexts.unlocked,
            label2: (params) => params.isLocked ? null : this.titleIconHoverTexts.unlocked2,
            callback: (action, params) => this.gridActionClicked(action, params)
          }
        ],
        gridRightActions: [
          {
            name: 'add-comment',
            icon: (params) => params.isFlagged ? 'mdi-flag' : params.commentActivity ? 'mdi-comment-text-multiple' : 'mdi-comment-multiple-outline',
            class: (params) => params.isFlagged ? 'flagged' : '',
            label: 'Add, Flag or Resolve Comments',
            callback: (action, params) => this.gridActionClicked(action, params)
          },
          {
            name: 'open-assumptions',
            icon: (params) => params.assumptionActivity ? 'mdi-comment-text' : 'mdi-comment-outline',
            label: 'Assumptions',
            callback: (action, params) => this.gridActionClicked(action, params)
          }
        ],
        gridActions: [
          {
            name: 'add-comment',
            icon: (params) => params.isFlagged ? 'mdi-flag' : params.commentActivity ? 'mdi-comment-text-multiple' : 'mdi-comment-multiple-outline',
            label: 'Add, Flag or Resolve Comments',
            callback: (action, params) => this.gridActionClicked(action, params)
          },
          {
            name: 'open-assumptions',
            icon: 'mdi-comment-alert',
            label: 'Add Assumption or View Calculation',
            callback: (action, params) => this.gridActionClicked(action, params)
          },
          null,//separator
          {
            name: 'add-row',
            icon: 'mdi-plus',
            label: 'Add row',
            callback: (action, params) => this.gridActionClicked(action, params)
          },
          {
            name: 'delete-rows',
            icon: 'mdi-delete',
            label: 'Delete Row Options',
            callback: (action, params) => this.gridActionClicked(action, params)
          },
          null,//separator
          {
            name: 'clear-reset',
            icon: 'cached',
            label: 'Clear & Reset',
            callback: (action, params) => this.gridActionClicked(action, params)
          }
        ],
        isCommentsDialogOpen: false,
        commentService: null,
        isDeleteRowsDialogOpen: false,
        deletingGLAccountRowsOptions: [],
        addingRowsForGLAccountId: null,
        isAddWorkingRowsDialogOpen: false,
        isCreateCalculationDialogOpen: false,
        selectedGLAccounts: {},
        anySelectedHasBulkCalcs: false,
        clearingGLAccountId: null,
        estimatedFutureHistoricalBasis: null,
        isAssumptionsDialogOpen: false,
        assumptionStatement: '',
        assumptionCalculations: [],
        editingGLAccountId: null,
        lastScrolledPosition: [],
        pendingNavigationCallback: null,
        isReforecast: false,
        lastCellEditedEvent: null,
        pastedData: null,
        notEditableReason: null,
        notEditableReasons: {
          generic: null,
          bulkEditable: 'This GL Account\'s budget numbers were populated with a Global formula. To edit these numbers, you will need the permission "Edit Global Formulas". Please speak with your administrator for that access.',
          locked: 'This GL Account has been locked. To make changes to this account will require the permission to Edit Locked GL Accounts.',
          cannotLockUnlock: 'The "Lock/Unlock" permission is required to lock or unlock GL accounts.'
        },
        showNotEditable: false,
        titleIconHoverTexts: {
          globalFormulas: 'The budget numbers for this working row were created through a Global formula.',
          locked: 'This GL Account has been locked. To make changes to this account will require the permission to Edit Locked GL Accounts.',
          unlocked: 'Locking this GL Account will prevent changes to the budget numbers and working rows unless the user has the permission to Edit Locked GL Accounts.',
          unlocked2: 'If this is a Gross Potential Rent, Vacancy Loss, or Gain/Loss to Lease GL Account, this will also lock the Market Rent and Occupancy sections.'
        },
        hasEditGlobalFormulaPermission: false,
        hasEditLockedGLPermission: false,
        hasLockUnlockGLPermission: false,
        pageSize: +(sessionStorage.getItem('budgets-pro-page-size')) || 25,
        currentPage: 1,
        pageMin: 0,
        pageMax: 1,
        glAccountsTotal: 1,
        glPagesMax: 1
      }
    },
    methods: {
      ...mapActions(['changeLoading', 'changeSelectedGroupName', 'changeIsEditing']),
      setGLPageSize() {
        if (this.pageSize == '') return;

        var resetInput = false;

        if (isNaN(this.pageSize || '') || this.pageSize <= 0 || this.pageSize > 100) {
          resetInput = true;
          this.pageSize = 25;
        }

        this.pageSize = Math.max(this.pageSize || 1, 1);
        this.pageSize = Math.min(this.pageSize || 1, 100);

        sessionStorage.setItem('budgets-pro-page-size', this.pageSize);

        this.pageMin = this.pageSize * (this.currentPage - 1);
        this.pageMax = this.pageSize * (this.currentPage);

        var pagedGLAccountData = [];
        var pagedGLDict = {};
        var GLDict = {};

          for (var i = 0; i < this.glAccountData.length; i++) {
              var data = this.glAccountData[i];
              GLDict[data.glAccountId] = 1;
              if (pagedGLDict[data.glAccountId]) {
                  pagedGLAccountData.push(data);
              }
              else {

                  if (Object.keys(GLDict).length <= this.pageMin) {
                      continue;
                  }
                  if (Object.keys(GLDict).length > this.pageMax) {
                      continue;
                  }

                  pagedGLDict[data.glAccountId] = 1;
                  pagedGLAccountData.push(data);

              }
          }

          let renderedRows = [];
          this.glAccountDataGridOptions.api.forEachNode(node => {renderedRows.push(node.data); });

          const pagedTransaction = {
              add: pagedGLAccountData,
              remove: renderedRows
          };
          this.glAccountDataGridOptions.api.applyTransactionAsync(pagedTransaction);

        this.glAccountsTotal = Object.keys(GLDict).length;
        this.pageMax = Math.min(this.pageMax, this.glAccountsTotal);

        this.glPagesMax = Math.ceil(this.glAccountsTotal / this.pageSize);

        if (!resetInput && this.currentPage > this.glPagesMax) {
          this.currentPage = Math.min(this.currentPage, this.glPagesMax);
          this.setGLPageSize();
        }
        else if (resetInput) {
          this.pageSize = 1;
          setTimeout(() => { this.pageSize = 25});
        }

      },
async toggleWorkingRowLock(workingRowData) {
 
  if (!this.hasLockUnlockGLPermission) {
    this.displayLockedLockUnlockMessage();
    return;
  }
  if (workingRowData.parentGLIsLocked) {
    return;
  }
  const currentLockState = workingRowData.isLocked || false;
  const newLockState = !currentLockState;

  try {
    
    const result = await budgetGLAccountService.setWorkingRowLocked(this.budgetIterationId, workingRowData.budgetGLAccountWorkingRowId,newLockState);

    if (!result) {
      this.$eventHub.$emit('showAlertNotification', 'Failed to update lock status');
      return;
    }
    this.glAccountData.forEach((glRow) => {
      if (glRow.budgetGLAccountWorkingRowId === workingRowData.budgetGLAccountWorkingRowId) {
       
        glRow.isLocked = newLockState;     
        const effectiveIsLocked = glRow.parentGLIsLocked || newLockState;      
        glRow.isEditable = (!effectiveIsLocked || this.hasEditLockedGLPermission) &&
                           (!glRow.rowHasBulkCalculation || this.hasEditGlobalFormulaPermission);
        
        glRow.notEditableReason = effectiveIsLocked && !this.hasEditLockedGLPermission ? 
          this.notEditableReasons.locked : 
          (glRow.rowHasBulkCalculation && !this.hasEditGlobalFormulaPermission ? 
           this.notEditableReasons.bulkEditable : '');
      }
    }); 
    this.glAccountDataGridOptions.api.redrawRows();

    this.$eventHub.$emit('showAlertNotification', 
      `Working row ${newLockState ? "locked" : "unlocked"}`);
      
  } catch (error) {
    console.error('Error toggling working row lock:', error);
    this.$eventHub.$emit('showAlertNotification', 'Error updating lock status');
  }
},


      async loadUserSettings() {
        var userSettings = await UserService.getUserSettings(true);

        this.hasEditGlobalFormulaPermission = userSettings && userSettings.permissions && userSettings.permissions.EditGlobalFormulas && userSettings.permissions.EditGlobalFormulas.hasPermission;
        this.hasEditLockedGLPermission = userSettings && userSettings.permissions && userSettings.permissions.EditLockedGLAccounts && userSettings.permissions.EditLockedGLAccounts.hasPermission;
        this.hasLockUnlockGLPermission = userSettings && userSettings.permissions && userSettings.permissions.LockUnlockBudget && userSettings.permissions.LockUnlockBudget.hasPermission;
      },
      async loadBudgetSettings() {
        this.changeLoading(true);
        
        let settings = await budgetService.getBudgetSettings(this.budgetIterationId);
        this.fiscalYear = settings.year;
        this.fiscalYearStartMonth = settings.startMonth;
        this.status = settings.status;
        this.userCanEdit = settings.userCanEdit;
        this.userCanEditApproved = settings.userCanEditApproved;
        this.userCanEditPublished = settings.userCanEditPublished;
        this.accountingBasisId = settings.accountingBasisId;

        this.isReforecast = Boolean(settings.isReforecast);

        this.refreshDataFilter();

        switch (settings.estimateFutureHistoricalsOption) {
          case 1:
            this.estimatedFutureHistoricalBasis = 'annualizing current year';
            break;
          case 2:
            this.estimatedFutureHistoricalBasis = 'using last year actuals';
            break;
          case 3:
            this.estimatedFutureHistoricalBasis = 'using current year budget';
            break;
          case 4:
            this.estimatedFutureHistoricalBasis = 'using current year reforecast';
            break;
          case 5:
            this.estimatedFutureHistoricalBasis = 'do not Estimate';
            break;
        }
        this.budgetIterationLastModifiedTimestamp = settings.lastModifiedTimestamp;
        var detailColumnWidth = 90;

        // setup column headers
        let months = this.getFiscalYearMonths(this.fiscalYearStartMonth);
        this.availableColumns = [
          { headerName: "", field: "label", width: 210, suppressSizeToFit: true, editable: this.isTitleCellEditable, cellRendererFramework: 'GLTitleCell', cellRendererParams: { openCalculator: this.openRowCalculator,toggleWorkingRowLock: this.toggleWorkingRowLock  }, cellStyle: { textAlign: 'center', fontSize: '12px', fontWeight: 'bold', fontFamily: '"Roboto", sans-serif', backgroundColor: '#e2e2e2', opacity: '1' } },
          { headerName: months[0], field: "periodIndex00Amount", width: detailColumnWidth, suppressSizeToFit: true, cellRendererFramework: 'LinkableCell', editable: this.isCellEditable, valueParser: this.parseBudgetValue, valueFormatter: this.formatBudgetValueWithHistoricalCellIndicator, cellStyle: this.getCellStyle },
          { headerName: months[1], field: "periodIndex01Amount", width: detailColumnWidth, suppressSizeToFit: true, cellRendererFramework: 'LinkableCell', editable: this.isCellEditable, valueParser: this.parseBudgetValue, valueFormatter: this.formatBudgetValueWithHistoricalCellIndicator, cellStyle: this.getCellStyle },
          { headerName: months[2], field: "periodIndex02Amount", width: detailColumnWidth, suppressSizeToFit: true, cellRendererFramework: 'LinkableCell', editable: this.isCellEditable, valueParser: this.parseBudgetValue, valueFormatter: this.formatBudgetValueWithHistoricalCellIndicator, cellStyle: this.getCellStyle },
          { headerName: months[3], field: "periodIndex03Amount", width: detailColumnWidth, suppressSizeToFit: true, cellRendererFramework: 'LinkableCell', editable: this.isCellEditable, valueParser: this.parseBudgetValue, valueFormatter: this.formatBudgetValueWithHistoricalCellIndicator, cellStyle: this.getCellStyle },
          { headerName: months[4], field: "periodIndex04Amount", width: detailColumnWidth, suppressSizeToFit: true, cellRendererFramework: 'LinkableCell', editable: this.isCellEditable, valueParser: this.parseBudgetValue, valueFormatter: this.formatBudgetValueWithHistoricalCellIndicator, cellStyle: this.getCellStyle },
          { headerName: months[5], field: "periodIndex05Amount", width: detailColumnWidth, suppressSizeToFit: true, cellRendererFramework: 'LinkableCell', editable: this.isCellEditable, valueParser: this.parseBudgetValue, valueFormatter: this.formatBudgetValueWithHistoricalCellIndicator, cellStyle: this.getCellStyle },
          { headerName: months[6], field: "periodIndex06Amount", width: detailColumnWidth, suppressSizeToFit: true, cellRendererFramework: 'LinkableCell', editable: this.isCellEditable, valueParser: this.parseBudgetValue, valueFormatter: this.formatBudgetValueWithHistoricalCellIndicator, cellStyle: this.getCellStyle },
          { headerName: months[7], field: "periodIndex07Amount", width: detailColumnWidth, suppressSizeToFit: true, cellRendererFramework: 'LinkableCell', editable: this.isCellEditable, valueParser: this.parseBudgetValue, valueFormatter: this.formatBudgetValueWithHistoricalCellIndicator, cellStyle: this.getCellStyle },
          { headerName: months[8], field: "periodIndex08Amount", width: detailColumnWidth, suppressSizeToFit: true, cellRendererFramework: 'LinkableCell', editable: this.isCellEditable, valueParser: this.parseBudgetValue, valueFormatter: this.formatBudgetValueWithHistoricalCellIndicator, cellStyle: this.getCellStyle },
          { headerName: months[9], field: "periodIndex09Amount", width: detailColumnWidth, suppressSizeToFit: true, cellRendererFramework: 'LinkableCell', editable: this.isCellEditable, valueParser: this.parseBudgetValue, valueFormatter: this.formatBudgetValueWithHistoricalCellIndicator, cellStyle: this.getCellStyle },
          { headerName: months[10], field: "periodIndex10Amount", width: detailColumnWidth,suppressSizeToFit: true, cellRendererFramework: 'LinkableCell', editable: this.isCellEditable, valueParser: this.parseBudgetValue, valueFormatter: this.formatBudgetValueWithHistoricalCellIndicator, cellStyle: this.getCellStyle },
          { headerName: months[11], field: "periodIndex11Amount", width: detailColumnWidth,suppressSizeToFit: true, cellRendererFramework: 'LinkableCell', editable: this.isCellEditable, valueParser: this.parseBudgetValue, valueFormatter: this.formatBudgetValueWithHistoricalCellIndicator, cellStyle: this.getCellStyle },
          { headerName: "TOTAL/AVG", field: "totalAverage", width: detailColumnWidth, suppressSizeToFit: true, valueFormatter: this.formatBudgetValueDollar, cellStyle: this.getCellStyle },
          {
            headerName: 'CALCULATIONS',
            resizable: true,
            wrapText: true,
            autoHeight: true,
            cellStyle: this.getCellStyle,
            width: 20 * 15, suppressSizeToFit: true, headerClass: 'summary-header', cellClass: 'calculation-cell',
            field: 'calculationSentences',
            hide: !this.showCalculations
          },
          {
            headerName: 'ASSUMPTIONS',
            resizable: true,
            wrapText: true,
            autoHeight: true,
            cellStyle: this.getCellStyle,
            width: 20 * 15, suppressSizeToFit: true, headerClass: 'summary-header', cellClass: 'assumption-cell',
            field: 'assumptionStatement',
            hide: !this.showAssumptions
          }
        ];

        // align grids
        this.groupDataGridOptions.alignedGrids = [this.glAccountDataGridOptions];
        this.glAccountDataGridOptions.alignedGrids = [this.groupDataGridOptions];

        // summary columns
        this.initSummaryColumns();

        this.changeLoading(false);
      },
      initSummaryColumns() {
        var columnsAdded = 0;
        var columnWidth = 29;

        this.summaryColumns = [
          /* Row label */
          {
            headerName: "", field: "label",
            cellRendererFramework: 'GLNameNumberCell',
            width: 4 * columnWidth * 2,
            wrapText: false,
            autoHeight: false,
            suppressSizeToFit: true,
            columnStyle: {}, headerClass: 'blank-header', cellClass: 'label-cell'
          },
          /* budget/Reforecast to actuals */
          {
            headerName: `${this.isReforecast ? 'REFORECAST' : 'BUDGET'} TO ${this.isReforecast ? 'CURRENT YEAR ' : ''}ACTUALS: PER DOLLAR`,
            suppressSizeToFit: true, headerClass: 'summary-header to-actuals-header',
            children: [
              {
                headerName: `${this.fiscalYear} ${this.isReforecast ? 'REFORECAST' : 'BUDGET'}`,
                width: (5 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-actuals-header', cellClass: 'left-border-cell to-actuals-cell',
                field: this.isReforecast ? 'reforecastToActuals_currentYear_reforecast' : 'budgetToActuals_currentYear_budget', valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: ((this.isReforecast ? this.fiscalYear : this.fiscalYear - 1)) + ' ACTUALS',
                width: (5 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-actuals-header', cellClass: 'to-actuals-cell',
                field: this.isReforecast ? 'reforecastToActuals_currentYear_actuals' : 'budgetToActuals_priorYear_actuals', valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: 'VAR $',
                width: (5 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-actuals-header', cellClass: 'to-actuals-cell',
                field: this.isReforecast ? 'reforecastToActuals_variance_dollars' : 'budgetToActuals_variance_dollars', valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: 'VAR %',
                width: (5 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-actuals-header', cellClass: 'to-actuals-cell',
                field: this.isReforecast ? 'reforecastToActuals_variance_percent' : 'budgetToActuals_variance_percent', valueFormatter: this.formatBudgetValueRoundedPercent
              }
            ]
          },
          {
            headerName: `${this.isReforecast ? 'REFORECAST' : 'BUDGET'} TO ${this.isReforecast ? 'CURRENT YEAR ' : ''}ACTUALS: PER UNIT`,
            suppressSizeToFit: true, headerClass: 'summary-header to-actuals-header',
            hide: !this.showUnitDetails,
            children: [
              {
                headerName: `${this.fiscalYear} ${this.isReforecast ? 'REFORECAST' : 'BUDGET'}`,
                width: (5 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-actuals-header', cellClass: 'left-border-cell to-actuals-cell',
                field: this.isReforecast ? 'reforecastToActuals_currentYear_reforecast_perUnit' : 'budgetToActuals_currentYear_budget_perUnit', valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: ((this.isReforecast ? this.fiscalYear : this.fiscalYear - 1)) + ' ACTUALS',
                width: (5 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-actuals-header', cellClass: 'to-actuals-cell',
                field: this.isReforecast ? 'reforecastToActuals_currentYear_actuals_perUnit' : 'budgetToActuals_priorYear_actuals_perUnit', valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: 'VAR $',
                width: (5 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-actuals-header', cellClass: 'to-actuals-cell',
                field: this.isReforecast ? 'reforecastToActuals_perUnit_variance_dollars' : 'budgetToActuals_perUnit_variance_dollars', valueFormatter: this.formatBudgetValueDollar
              }
            ]
          },
          {
            headerName: `${this.isReforecast ? 'REFORECAST' : 'BUDGET'} TO ${this.isReforecast ? 'CURRENT YEAR ' : ''}ACTUALS: PER SQUARE FOOT`,
            suppressSizeToFit: true, headerClass: 'summary-header to-actuals-header',
            hide: !this.showSqftDetails,
            children: [
              {
                headerName: `${this.fiscalYear} ${this.isReforecast ? 'REFORECAST' : 'BUDGET'}`,
                width: (5 * columnWidth) - ((columnWidth * columnsAdded) / 3),
                suppressSizeToFit: true, headerClass: 'summary-header to-actuals-header', cellClass: 'left-border-cell to-actuals-cell',
                field: this.isReforecast ? 'reforecastToActuals_currentYear_reforecast_perSqft' : 'budgetToActuals_currentYear_budget_perSqft', valueFormatter: this.formatBudgetValueDollarWithCents
              },
              {
                headerName: ((this.isReforecast ? this.fiscalYear : this.fiscalYear - 1)) + ' ACTUALS',
                width: (5 * columnWidth) - ((columnWidth * columnsAdded) / 3),
                suppressSizeToFit: true, headerClass: 'summary-header to-actuals-header', cellClass: 'to-actuals-cell',
                field: this.isReforecast ? 'reforecastToActuals_currentYear_actuals_perSqft' : 'budgetToActuals_priorYear_actuals_perSqft', valueFormatter: this.formatBudgetValueDollarWithCents
              },
              {
                headerName: 'VAR $',
                width: (5 * columnWidth) - ((columnWidth * columnsAdded) / 3),
                suppressSizeToFit: true, headerClass: 'summary-header to-actuals-header', cellClass: 'to-actuals-cell',
                field: this.isReforecast ? 'reforecastToActuals_perSqft_variance_dollars' : 'budgetToActuals_perSqft_variance_dollars', valueFormatter: this.formatBudgetValueDollarWithCents
              }
            ]
          },
          /* Budget/Reforecast to Prior/Current budget */
          {
            headerName: `${this.isReforecast ? 'REFORECAST' : 'BUDGET'} TO ${this.isReforecast ? '' : 'PRIOR '}BUDGET: PER DOLLAR`,
            suppressSizeToFit: true, headerClass: 'summary-header to-prior-header',
            hide: (this.isReforecast ? !this.showCurrentYrBudgetDetails : !this.showPriorYrBudgetDetails),
            children: [
              {
                headerName: `${this.fiscalYear} ${this.isReforecast ? 'REFORECAST' : 'BUDGET'}`,
                width: (4 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-prior-header', cellClass: 'left-border-cell to-prior-cell',
                field: this.isReforecast ? 'reforecastToBudget_currentYear_reforecast' : 'budgetToPriorBudget_currentYear_budget', valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: ((this.isReforecast ? this.fiscalYear : this.fiscalYear - 1)) + ' BUDGET',
                width: (4 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-prior-header', cellClass: 'to-prior-cell',
                field: this.isReforecast ? 'reforecastToBudget_currentYear_budget' : 'budgetToPriorBudget_priorYear_budget', valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: 'VAR $',
                width: (4 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-prior-header', cellClass: 'to-prior-cell',
                field: this.isReforecast ? 'reforecastToBudget_variance_dollars' : 'budgetToPriorBudget_variance_dollars', valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: 'VAR %',
                width: (4 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-prior-header', cellClass: 'to-prior-cell',
                field: this.isReforecast ? 'reforecastToBudget_variance_percent' : 'budgetToPriorBudget_variance_percent', valueFormatter: this.formatBudgetValueRoundedPercent
              }
            ]
          },
          {
            headerName: `${this.isReforecast ? 'REFORECAST' : 'BUDGET'} TO ${this.isReforecast ? '' : 'PRIOR '}BUDGET: PER UNIT`,
            suppressSizeToFit: true, headerClass: 'summary-header to-prior-header',
            hide: !this.showUnitDetails || (this.isReforecast ? !this.showCurrentYrBudgetDetails : !this.showPriorYrBudgetDetails),
            children: [
              {
                headerName: `${this.fiscalYear} ${this.isReforecast ? 'REFORECAST' : 'BUDGET'}`,
                width: (4 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-prior-header', cellClass: 'left-border-cell to-prior-cell',
                field: this.isReforecast ? 'reforecastToBudget_currentYear_reforecast_perUnit' : 'budgetToPriorBudget_currentYear_budget_perUnit', valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: ((this.isReforecast ? this.fiscalYear : this.fiscalYear - 1)) + ' BUDGET',
                width: (4 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-prior-header', cellClass: 'to-prior-cell',
                field: this.isReforecast ? 'reforecastToBudget_currentYear_budget_perUnit' : 'budgetToPriorBudget_priorYear_budget_perUnit', valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: 'VAR $',
                width: (4 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-prior-header', cellClass: 'to-prior-cell',
                field: this.isReforecast ? 'reforecastToBudget_perDollar_variance_dollars' : 'budgetToPriorBudget_perDollar_variance_dollars', valueFormatter: this.formatBudgetValueDollar
              }
            ]
          },
          {
            headerName: `${this.isReforecast ? 'REFORECAST' : 'BUDGET'} TO ${this.isReforecast ? '' : 'PRIOR '}BUDGET: PER SQUARE FOOT`,
            suppressSizeToFit: true, headerClass: 'summary-header to-prior-header',
            hide: !this.showSqftDetails || (this.isReforecast ? !this.showCurrentYrBudgetDetails : !this.showPriorYrBudgetDetails),
            children: [
              {
                headerName: `${this.fiscalYear} ${this.isReforecast ? 'REFORECAST' : 'BUDGET'}`,
                width: (4 * columnWidth) - ((columnWidth * columnsAdded) / 3),
                suppressSizeToFit: true, headerClass: 'summary-header to-prior-header', cellClass: 'left-border-cell to-prior-cell',
                field: this.isReforecast ? 'reforcastToBudget_currentYear_reforecast_perSqft' : 'budgetToPriorBudget_currentYear_budget_perSqft', valueFormatter: this.formatBudgetValueDollarWithCents
              },
              {
                headerName: ((this.isReforecast ? this.fiscalYear : this.fiscalYear - 1)) + ' BUDGET',
                width: (4 * columnWidth) - ((columnWidth * columnsAdded) / 3),
                suppressSizeToFit: true, headerClass: 'summary-header to-prior-header', cellClass: 'to-prior-cell',
                field: this.isReforecast ? 'reforecastToBudget_currentYear_budget_perSqft' : 'budgetToPriorBudget_priorYear_budget_perSqft', valueFormatter: this.formatBudgetValueDollarWithCents
              },
              {
                headerName: 'VAR $',
                width: (4 * columnWidth) - ((columnWidth * columnsAdded) / 3),
                suppressSizeToFit: true, headerClass: 'summary-header to-prior-header', cellClass: 'to-prior-cell',
                field: this.isReforecast ? 'reforecastToBudget_perSqft_variance_dollars' : 'budgetToPriorBudget_perSqft_variance_dollars' , valueFormatter: this.formatBudgetValueDollarWithCents
              }
            ]
          },
          /* Budget to reforecast */
          {
            headerName: "BUDGET TO REFORECAST: PER DOLLAR",
            suppressSizeToFit: true, headerClass: 'summary-header to-reforecast-header',
            hide: !this.showPriorYrReforecastDetails,
            children: [
              {
                headerName: this.fiscalYear + ' BUDGET',
                autoHeight: true,
                width: (4 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-reforecast-header', cellClass: 'left-border-cell to-reforecast-cell',
                field: "budgetToReforecast_currentYear_budget", valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: (this.fiscalYear - 1) + ' REFORECAST',
                width: (4 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-reforecast-header', cellClass: 'to-reforecast-cell',
                field: "budgetToReforecast_priorYear_reforecast", valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: 'VAR $',
                width: (4 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-reforecast-header', cellClass: 'to-reforecast-cell',
                field: "budgetToReforecast_variance_dollars", valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: 'VAR %',
                width: (4 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-reforecast-header', cellClass: 'to-reforecast-cell',
                field: "budgetToReforecast_variance_percent", valueFormatter: this.formatBudgetValueRoundedPercent
              }
            ]
          },
          {
            headerName: "BUDGET TO REFORECAST: PER UNIT",
            suppressSizeToFit: true, headerClass: 'summary-header to-reforecast-header',
            hide: !this.showUnitDetails || !this.showPriorYrReforecastDetails,
            children: [
              {
                headerName: this.fiscalYear + ' BUDGET',
                width: (4 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-reforecast-header', cellClass: 'left-border-cell to-reforecast-cell',
                field: "budgetToReforecast_currentYear_budget_perUnit", valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: (this.fiscalYear - 1) + ' REFORECAST',
                width: (4 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-reforecast-header', cellClass: 'to-reforecast-cell',
                field: "budgetToReforecast_priorYear_reforecast_perUnit", valueFormatter: this.formatBudgetValueDollar
              },
              {
                headerName: 'VAR $',
                width: (4 * columnWidth) - (columnWidth * columnsAdded),
                suppressSizeToFit: true, headerClass: 'summary-header to-reforecast-header', cellClass: 'to-reforecast-cell',
                field: "budgetToReforecast_perUnit_variance_dollars", valueFormatter: this.formatBudgetValueDollar
              }
            ]
          },
          {
            headerName: "BUDGET TO REFORECAST: PER SQUARE FOOT",
            suppressSizeToFit: true, headerClass: 'summary-header to-reforecast-header',
            hide: !this.showSqftDetails || !this.showPriorYrReforecastDetails,
            children: [
              {
                headerName: this.fiscalYear + ' BUDGET',
                width: (4 * columnWidth) - ((columnWidth * columnsAdded) / 3),
                suppressSizeToFit: true, headerClass: 'summary-header to-reforecast-header', cellClass: 'left-border-cell to-reforecast-cell',
                field: "budgetToReforecast_currentYear_budget_perSqft", valueFormatter: this.formatBudgetValueDollarWithCents
              },
              {
                headerName: (this.fiscalYear - 1) + ' REFORECAST',
                width: (4 * columnWidth) - ((columnWidth * columnsAdded) / 3),
                suppressSizeToFit: true, headerClass: 'summary-header to-reforecast-header', cellClass: 'to-reforecast-cell',
                field: "budgetToReforecast_priorYear_reforecast_perSqft", valueFormatter: this.formatBudgetValueDollarWithCents
              },
              {
                headerName: 'VAR $',
                width: (4 * columnWidth) - ((columnWidth * columnsAdded) / 3),
                suppressSizeToFit: true, headerClass: 'summary-header to-reforecast-header', cellClass: 'to-reforecast-cell',
                field: "budgetToReforecast_perSqft_variance_dollars", valueFormatter: this.formatBudgetValueDollarWithCents
              }
            ]
          },
          // Calculations and Assumptions
          {
            headerName: '',
            headerClass: 'summary-header calc-assumptions-header',
            children: [{
              headerName: 'CALCULATIONS',
              resizable: true,
              wrapText: true,
              autoHeight: true,
              width: 20 * 15, suppressSizeToFit: true, headerClass: 'summary-header calc-assumptions-header', cellClass: 'calculation-cell left-border-cell',
              field: 'calculationSentences',
              hide: !this.showCalculations
            }]
          },
          {
            headerName: '',
            headerClass: 'summary-header calc-assumptions-header',
            children: [{
              headerName: 'ASSUMPTIONS',
              resizable: true,
              wrapText: true,
              autoHeight: true,
              width: 20 * 15, suppressSizeToFit: true, headerClass: 'summary-header calc-assumptions-header', cellClass: 'assumption-cell left-border-cell',
              field: 'assumptionStatement',
              hide: !this.showAssumptions
            }]
          }
        ];
      },
      getCellStyle(params) {
        var style = { textAlign: 'center' };
        if (params && this.isLockedOut(params)) {
          style.backgroundColor = 'var(--v-resGrey-lighten3)';
        }
        return style;
      },
      async loadBudgetGLAccountDetail(responseData, workingRows, calculations) {
        // group totals
        const newGroupData = [];
        newGroupData.push(this.initGroupTotalRow(responseData.groupId, responseData.budgetTotals, false));
        if (responseData.priorYearActuals) {
          newGroupData.push(this.initGroupTotalRow(responseData.groupId, responseData.priorYearActuals, true));
        }
        if (responseData.priorPriorYearActuals) {
          newGroupData.push(this.initGroupTotalRow(responseData.groupId, responseData.priorPriorYearActuals, true));
        }

        this.groupData = newGroupData;

        // gl accounts
        const newGLAccountData = [];
        responseData.budgetGLAccounts.forEach((bgla) => {
          const glWorkingRows = workingRows && workingRows.length > 0 ? 
            workingRows.filter((row) => { return row.glAccountId.toLowerCase() == bgla.glAccountId.toLowerCase(); }) 
            : [];
          const glCalculations = calculations.filter((calc) => calc.glAccountId == bgla.glAccountId);

          newGLAccountData.push(this.initGLAccountHeaderRow(bgla.glAccountId, bgla.number, bgla.name, bgla.hasComments, bgla.hasUnresolvedComments, glWorkingRows.length > 0, bgla.assumptionStatement, glCalculations, bgla.url, bgla.isLocked));
          newGLAccountData.push(this.initGLAccountRow(bgla.glAccountId, bgla.number, bgla.name, bgla.budgets, false, glWorkingRows.length === 0, glWorkingRows.length > 0, glWorkingRows, bgla.assumptionStatement, glCalculations, bgla.isLocked));

          glWorkingRows.forEach((row) => {                                       
            const wrCalculations = glCalculations.filter((calc) => (calc.budgetGLAccountWorkingRowId || '') == row.budgetGLAccountWorkingRowId);
            newGLAccountData.push(this.initGLAccountSubRow(bgla.glAccountId, bgla.number, bgla.name, row, row, false, true, true, row.budgetGLAccountWorkingRowId, wrCalculations, bgla.isLocked));
          });

          if (bgla.priorYearActuals) {
            this.initCollapsibleGLAccountRow(bgla.glAccountId, bgla.number, bgla.name, bgla.priorYearActuals, true, false, bgla.assumptionStatement, glCalculations, bgla.isLocked).forEach((row) => { newGLAccountData.push(row); });
          }
          if (bgla.priorPriorYearActuals) {
            this.initCollapsibleGLAccountRow(bgla.glAccountId, bgla.number, bgla.name, bgla.priorPriorYearActuals, true, false, bgla.assumptionStatement, glCalculations, bgla.isLocked).forEach((row) => { newGLAccountData.push(row); });
          }
        });

        const viewPort = document.querySelector('.gl-detail-grid [ref="eBodyViewport"]');
        const viewPortScrollPosition = viewPort ? viewPort.scrollTop : null;
        this.glAccountData = newGLAccountData;

        if (viewPort) {
          setTimeout(() => {
            viewPort.scrollTop = viewPortScrollPosition;
          });
        }
      },
      async loadBudgetGLAccountSummary(responseData, calculations) {
        // group totals
        this.groupSummaryData = [];
        this.groupSummaryData.push(this.initSummaryGroupTotalRow(responseData.groupId, responseData, false));

        // gl accounts
        responseData.budgetGLAccounts.forEach((bgla) => {
          var glCalculations = calculations.filter((calc) => calc.glAccountId == bgla.glAccountId);
          this.groupSummaryData.push(this.initSummaryGLAccountRow(bgla.glAccountId, bgla, false, true, glCalculations));
        });
      },
      getTimestamp() {
        const now = new Date();

        // Get minutes, seconds, and milliseconds
        const minutes = now.getMinutes();
        const seconds = now.getSeconds();
        const milliseconds = now.getMilliseconds();

        // Print in the format MM:SS:MMM
        return`${minutes}:${seconds}:${milliseconds}`;
      },
      async loadBudgetGLAccounts() {
        this.changeLoading(true);

        this.selectedGLAccounts = {};
        //console.log('loadBudgetGLAccounts - getBudgetGLAccounts - START', this.getTimestamp());
        let response = await budgetGLAccountService.getBudgetGLAccounts(this.budgetIterationId, this.groupId, this.historicalYearsDisplayCount > 0);

        if (!response || !response.groups.length) {
          this.changeLoading(false);
          return;
        }

        //console.log('loadBudgetGLAccounts - getBudgetGLAccounts - END', this.getTimestamp());

        let responseData = response.groups[0];

        this.showCurrentYrBudgetDetails = responseData.currentBudgetExists;
        this.showPriorYrBudgetDetails = responseData.priorBudgetExists;
        this.showPriorYrReforecastDetails = !this.isReforecast && responseData.priorYearReforecastExists;

        //console.log('loadBudgetGLAccounts - getWorkingRows - START', this.getTimestamp());
        let workingRows = await budgetGLAccountService.getWorkingRows(this.budgetIterationId) || { data: [] };
        //console.log('loadBudgetGLAccounts - getWorkingRows - END', this.getTimestamp());
        //console.log('loadBudgetGLAccounts - getCalculations - START', this.getTimestamp());
        let calculations = await budgetGLAccountService.getCalculations(this.budgetIterationId, this.groupId) || { data: [] };
        //console.log('loadBudgetGLAccounts - getCalculations - END', this.getTimestamp());

        this.changeSelectedGroupName(responseData.name);

        //console.log('loadBudgetGLAccounts - loadBudgetGLAccountDetail - START', this.getTimestamp());
        await this.loadBudgetGLAccountDetail(responseData, workingRows.data, calculations.data);
        //console.log('loadBudgetGLAccounts - loadBudgetGLAccountDetail - END', this.getTimestamp());
        //console.log('loadBudgetGLAccounts - loadBudgetGLAccountSummary - START', this.getTimestamp());
        await this.loadBudgetGLAccountSummary(responseData, calculations.data);
        //console.log('loadBudgetGLAccounts - loadBudgetGLAccountSummary - END', this.getTimestamp());

        this.resizeRows();
        this.currentPage = 1;
        this.setGLPageSize();

        this.changeLoading(false);

        //console.log('waiting to render');
      },
      async refreshGroupTotals() {
        let t = this;
        
        let responseData = (await budgetService.getBudgetGroupTotals(t.budgetIterationId, t.groupId)).groups[0].budgetTotals;
        
        let groupTotalRowData = t.groupDataGridOptions.api.getRowNode(0).data;

        // set period amounts
        for (let i = 0; i < 12; i++) {
          let amount = t.getPeriodIndexAmount(responseData, i);
          t.setRowNodePeriodIndexAmount(groupTotalRowData, i, amount);
        }

        // set total
        groupTotalRowData.total = responseData.total;
        groupTotalRowData.totalAverage = responseData.total;

        // update view
        t.groupDataGridOptions.api.applyTransactionAsync({ update: [groupTotalRowData] });

        // refresh summary
        if (!t.summaryRefreshIsQueued) {
          t.summaryRefreshIsQueued = true;
          setTimeout(async () => {
            t.summaryRefreshIsQueued = false;
            let data = (await budgetGLAccountService.getBudgetGLAccounts(t.budgetIterationId, t.groupId, t.historicalYearsDisplayCount > 0)).groups[0];
            let calculations = await budgetGLAccountService.getCalculations(t.budgetIterationId, t.groupId) || { data: [] };
            await t.loadBudgetGLAccountSummary(data, calculations.data);
          }, 2500); // reduce refresh calls when multiple edits happen at once
        }
      },
      initGroupTotalRow(groupId, data, isHistorical) {
        return {
          isHeader: false,
          isGroupTotal: true,
          key: groupId + data.year,
          groupId: groupId,
          year: data.year,
          label: `${isHistorical ? 'HISTORICAL ACTUAL TOTAL' : 'PROJECTED BUDGET TOTAL'} ${data.year}`.trim(),
          commentActivity: data.hasComments,
          isFlagged: data.hasUnresolvedComments,
          periodIndex00Amount: data.periodIndex00.amount,
          periodIndex01Amount: data.periodIndex01.amount,
          periodIndex02Amount: data.periodIndex02.amount,
          periodIndex03Amount: data.periodIndex03.amount,
          periodIndex04Amount: data.periodIndex04.amount,
          periodIndex05Amount: data.periodIndex05.amount,
          periodIndex06Amount: data.periodIndex06.amount,
          periodIndex07Amount: data.periodIndex07.amount,
          periodIndex08Amount: data.periodIndex08.amount,
          periodIndex09Amount: data.periodIndex09.amount,
          periodIndex10Amount: data.periodIndex10.amount,
          periodIndex11Amount: data.periodIndex11.amount,

          periodIndexIsPopulatedFromFutureHistoricalSettings: [
            data.periodIndex00.isPopulatedFromFutureHistoricalSettings,
            data.periodIndex01.isPopulatedFromFutureHistoricalSettings,
            data.periodIndex02.isPopulatedFromFutureHistoricalSettings,
            data.periodIndex03.isPopulatedFromFutureHistoricalSettings,
            data.periodIndex04.isPopulatedFromFutureHistoricalSettings,
            data.periodIndex05.isPopulatedFromFutureHistoricalSettings,
            data.periodIndex06.isPopulatedFromFutureHistoricalSettings,
            data.periodIndex07.isPopulatedFromFutureHistoricalSettings,
            data.periodIndex08.isPopulatedFromFutureHistoricalSettings,
            data.periodIndex09.isPopulatedFromFutureHistoricalSettings,
            data.periodIndex10.isPopulatedFromFutureHistoricalSettings,
            data.periodIndex11.isPopulatedFromFutureHistoricalSettings
          ],

          totalAverage: this.calculateGLAccountTotal(data),
          priorYearTotal: data.priorYearTotal,
          total: data.total,
          varAmount: data.varianceAmount,
          varPercent: data.variancePercentage,
          revCostPerUnit: data.revenueOrCostPerUnit,
          unitCount: data.unitCount,
          sqFt: data.sqFt,
          isHistorical: isHistorical,

          hasHistoricalRecords: data.hasHistoricals,
          isHistoricalRecord: data.year < this.fiscalYear,
          showHistoricalRecords: this.historicalYearsDisplayCount > 0,
          historicalRecordAge: this.fiscalYear - data.year,
          url: isHistorical ? data.url : null
        }
      },
      initSummaryGroupTotalRow(groupId, data, isHistorical) {
        return {
          isHeader: false,
          isSummary: true,
          key: groupId + data.year,
          groupId: groupId,
          label: this.selectedGroupName,
          number: this.selectedGroupName,
          name: '',

          commentActivity: data.hasComments,
          isFlagged: data.hasUnresolvedComments,
          currentYear_budget_perDollar: data.budgetTotals.total,
          priorYear_budget_perDollar: data.budgetTotals.priorYearTotal,
          var_dollars_perDollar: data.budgetTotals.varianceAmount,
          var_percent_perDollar: data.budgetTotals.variancePercentage,
          currentYear_budget_perUnit: data.budgetTotals.amountPerUnit,
          priorYear_budget_perUnit: data.budgetTotals.priorYearAmountPerUnit,
          var_dollars_perUnit: data.budgetTotals.variancePerUnit,
          currentYear_budget_perSqft: data.budgetTotals.amountPerSqft,
          priorYear_budget_perSqft: data.budgetTotals.priorYearAmountPerSqft,
          var_dollars_perSqft: data.budgetTotals.variancePerSqft,

          budgetToActuals_currentYear_budget: data.budgetTotals.total,
          budgetToActuals_priorYear_actuals: data.budgetTotals.priorYearActualsTotal,
          budgetToActuals_variance_dollars: data.budgetTotals.budgetToActualsVarianceDollars,
          budgetToActuals_variance_percent: data.budgetTotals.budgetToActualsVariancePercent,

          budgetToActuals_currentYear_budget_perUnit: data.budgetTotals.budgetToActualsCurrentYearBudgetPerUnit,
          budgetToActuals_priorYear_actuals_perUnit: data.budgetTotals.budgetToActualsPriorYearActualsPerUnit,
          budgetToActuals_perUnit_variance_dollars: data.budgetTotals.budgetToActualsVariancePerUnitDollars,

          budgetToActuals_currentYear_budget_perSqft: data.budgetTotals.budgetToActualsCurrentYearBudgetPerSqft,
          budgetToActuals_priorYear_actuals_perSqft: data.budgetTotals.budgetToActualsPriorYearActualsPerSqft,
          budgetToActuals_perSqft_variance_dollars: data.budgetTotals.budgetToActualsVariancePerSqftDollars,

          budgetToPriorBudget_currentYear_budget: data.budgetTotals.total,
          budgetToPriorBudget_priorYear_budget: data.budgetTotals.priorYearBudgetTotal,
          budgetToPriorBudget_variance_dollars: data.budgetTotals.budgetToPriorBudgetVarianceDollars,
          budgetToPriorBudget_variance_percent: data.budgetTotals.budgetToPriorBudgetVariancePercent,

          budgetToPriorBudget_currentYear_budget_perUnit: data.budgetTotals.budgetToPriorBudgetCurrentYearBudgetPerUnit,
          budgetToPriorBudget_priorYear_budget_perUnit: data.budgetTotals.budgetToPriorBudgetPriorYearBudgetPerUnit,
          budgetToPriorBudget_perDollar_variance_dollars: data.budgetTotals.budgetToPriorBudgetVariancePerUnitDollars,

          budgetToPriorBudget_currentYear_budget_perSqft: data.budgetTotals.budgetToPriorBudgetCurrentYearBudgetPerSqft,
          budgetToPriorBudget_priorYear_budget_perSqft: data.budgetTotals.budgetToPriorBudgetPriorYearBudgetPerSqft,
          budgetToPriorBudget_perSqft_variance_dollars: data.budgetTotals.budgetToPriorBudgetVariancePerSqftDollars,

          budgetToReforecast_currentYear_budget: data.budgetTotals.total,
          budgetToReforecast_priorYear_reforecast: data.budgetTotals.priorYearReforecastTotal,
          budgetToReforecast_variance_dollars: data.budgetTotals.budgetToReforecastVarianceDollars,
          budgetToReforecast_variance_percent: data.budgetTotals.budgetToReforecastVariancePercent,

          budgetToReforecast_currentYear_budget_perUnit: data.budgetTotals.budgetToReforecastCurrentYearBudgetPerUnit,
          budgetToReforecast_priorYear_reforecast_perUnit: data.budgetTotals.budgetToReforecastPriorYearReforecastPerUnit,
          budgetToReforecast_perUnit_variance_dollars: data.budgetTotals.budgetToReforecastVariancePerUnitDollars,

          budgetToReforecast_currentYear_budget_perSqft: data.budgetTotals.budgetToReforecastCurrentYearBudgetPerSqft,
          budgetToReforecast_priorYear_reforecast_perSqft: data.budgetTotals.budgetToReforecastPriorYearReforecastPerSqft,
          budgetToReforecast_perSqft_variance_dollars: data.budgetTotals.budgetToReforecastVariancePerSqftDollars,


          reforecastToActuals_currentYear_reforecast: data.budgetTotals.total,
          reforecastToActuals_currentYear_actuals: data.budgetTotals.currentYearActualsTotal,
          reforecastToActuals_variance_dollars: data.budgetTotals.reforecastToActualsVarianceDollars,
          reforecastToActuals_variance_percent: data.budgetTotals.reforecastToActualsVariancePercent,

          reforecastToActuals_currentYear_reforecast_perUnit: data.budgetTotals.reforecastToActualsCurrentYearReforecastPerUnit,
          reforecastToActuals_currentYear_actuals_perUnit: data.budgetTotals.reforecastToActualsCurrentYearActualsPerUnit,
          reforecastToActuals_perUnit_variance_dollars: data.budgetTotals.reforecastToActualsVariancePerUnitDollars,

          reforecastToActuals_currentYear_reforecast_perSqft: data.budgetTotals.reforecastToActualsCurrentYearReforecastPerSqft,
          reforecastToActuals_currentYear_actuals_perSqft: data.budgetTotals.reforecastToActualsCurrentYearActualsPerSqft,
          reforecastToActuals_perSqft_variance_dollars: data.budgetTotals.reforecastToActualsVariancePerSqftDollars,

          reforecastToBudget_currentYear_reforecast: data.budgetTotals.total,
          reforecastToBudget_currentYear_budget: data.budgetTotals.currentYearBudgetTotal,
          reforecastToBudget_variance_dollars: data.budgetTotals.reforecastToBudgetVarianceDollars,
          reforecastToBudget_variance_percent: data.budgetTotals.reforecastToBudgetVariancePercent,

          reforecastToBudget_currentYear_reforecast_perUnit: data.budgetTotals.reforecastToBudgetCurrentYearReforecastPerUnit,
          reforecastToBudget_currentYear_budget_perUnit: data.budgetTotals.reforecastToBudgetCurrentYearBudgetPerUnit,
          reforecastToBudget_perDollar_variance_dollars: data.budgetTotals.reforecastToBudgetVariancePerUnitDollars,

          reforcastToBudget_currentYear_reforecast_perSqft: data.budgetTotals.reforecastToBudgetCurrentYearReforecastPerSqft,
          reforecastToBudget_currentYear_budget_perSqft: data.budgetTotals.reforecastToBudgetCurrentYearBudgetPerSqft,
          reforecastToBudget_perSqft_variance_dollars: data.budgetTotals.reforecastToBudgetVariancePerSqftDollars,

          priorYearTotal: data.priorYearTotal,
          total: data.total,
          varAmount: data.varianceAmount,
          varPercent: data.variancePercentage,
          revCostPerUnit: data.revenueOrCostPerUnit,
          unitCount: data.unitCount,
          sqFt: data.sqFt,
          isHistorical: isHistorical,

          hasHistoricalRecords: data.hasHistoricals,
          isHistoricalRecord: data.year < this.fiscalYear,
          showHistoricalRecords: this.historicalYearsDisplayCount > 0,
          historicalRecordAge: this.fiscalYear - data.year,
          url: isHistorical ? data.url : null
        }
      },
      initGLAccountHeaderRow(glAccountId, number, name, hasComments, hasUnresolvedComments, hasWorkingRows, assumptionStatement, calculations, url, isLocked) {
        var rowHasBulkCalculation = (calculations && !calculations.every((c) => c.type != 7));
        return {
          isHeader: true,
          hasWorkingRows: hasWorkingRows,
          rowHasBulkCalculation: rowHasBulkCalculation,
          key: glAccountId,
          commentActivity: hasComments,
          isFlagged: hasUnresolvedComments,
          glAccountId: glAccountId,
          glAccountNumber: number,
          glAccountName: number + ' - ' + name,
          label: number + ' - ' + name,
          isHistorical: false,
          actions: this.gridActions,
          leftActions: this.gridLeftActions,
          rightActions: this.gridRightActions,
          selectionChangedCallback: this.onRowSelectedChanged,
          isLocked: isLocked,

          assumptionStatement: assumptionStatement,
          assumptionActivity: assumptionStatement ? assumptionStatement.length > 0 : false,
          calculations: calculations,
          calculationSentences: ((calculations && (calculations[0] && calculations[0].naturalLanguageDescription) || '') + '\n' + (calculations[1] && calculations[1].naturalLanguageDescription || '') || '').trim() || 'No calculations',
          url: url
        }
      },
      initGLAccountSubRow(glAccountId, glAccountNumber, glAccountName, data, row, isHistorical, isEditable, isWorkingRow, key, calculations,parentGLIsLocked) {
        let description = `${row.subLabel || data.subLabel || data.title || data.label || data.name || ''}`.trim();
        if (description.length > 25) {
          description = description.substring(0, 25) + '...';
        }
  
         const workingRowIsLocked = Boolean(row.isLocked); 
         const effectiveIsLocked = parentGLIsLocked || workingRowIsLocked;
        var rowHasBulkCalculation = (calculations && !calculations.every((c) => c.type != 7));
        return {
          isHeader: false,
          key: key || (glAccountId + data.year),
          glAccountId: glAccountId,
          glAccountName: glAccountNumber + ' - ' + glAccountName,
          year: data.year,
          label: `${description} ${data.year || ''}`.trim(),
          isLocked: workingRowIsLocked,
          parentGLIsLocked: parentGLIsLocked,
          isCollapsed: localStorage.getItem(`${glAccountId + data.year}collapsed_subrow`),
          icon: rowHasBulkCalculation ? 'mdi-information-outline' : '',
          iconHoverText: this.titleIconHoverTexts.globalFormulas,
          rowHasBulkCalculation: rowHasBulkCalculation,

          periodIndex00Amount: row.periodIndex00.amount,
          periodIndex01Amount: row.periodIndex01.amount,
          periodIndex02Amount: row.periodIndex02.amount,
          periodIndex03Amount: row.periodIndex03.amount,
          periodIndex04Amount: row.periodIndex04.amount,
          periodIndex05Amount: row.periodIndex05.amount,
          periodIndex06Amount: row.periodIndex06.amount,
          periodIndex07Amount: row.periodIndex07.amount,
          periodIndex08Amount: row.periodIndex08.amount,
          periodIndex09Amount: row.periodIndex09.amount,
          periodIndex10Amount: row.periodIndex10.amount,
          periodIndex11Amount: row.periodIndex11.amount,
          totalAverage: this.calculateGLAccountTotal(row),

          priorYearTotal: data.priorYearTotal,
          total: row.total,
          varAmount: data.varianceAmount,
          varPercent: data.variancePercentage,
          revCostPerUnit: data.revenueOrCostPerUnit,
          unitCount: data.unitCount,
          sqFt: data.sqFt,
          isHistorical: isHistorical,
          isEditable: isEditable && 
                    (!rowHasBulkCalculation || this.hasEditGlobalFormulaPermission) && 
                    (!effectiveIsLocked || this.hasEditLockedGLPermission),
          notEditableReason: !isEditable ? this.notEditableReasons.generic : 
                           (effectiveIsLocked ? this.notEditableReasons.locked : 
                           (rowHasBulkCalculation && !this.hasEditGlobalFormulaPermission ? this.notEditableReasons.bulkEditable : '')),
          isWorkingRow: isWorkingRow,
          budgetGLAccountWorkingRowId: isWorkingRow ? data.budgetGLAccountWorkingRowId : null,
          calculations: calculations,
          calculationSentences: ((calculations && (calculations[0] && calculations[0].naturalLanguageDescription) || '') || '').trim() || '',
          
          hasHistoricalRecords: data.hasHistoricals,
          isHistoricalRecord: !isWorkingRow && (data.year < this.fiscalYear),
          showHistoricalRecords: this.historicalYearsDisplayCount > 0,
          historicalRecordAge: this.fiscalYear - data.year,
          url: isHistorical ? (data.url || null) : null
        }
      },
      initGLAccountRow(glAccountId, glAccountNumber, glAccountName, data, isHistorical, isEditable, isCollapsible, subRows, assumptionStatement, calculations, isLocked) {
        let aggregateSubRow = null;
        if (subRows && subRows.length > 0) {
          aggregateSubRow = this.aggregateSubRows(subRows);
        }
        var rowHasBulkCalculation = (calculations && !calculations.every((c) => c.type != 7));
        return {
          isGLMainRow: !isHistorical,
          glAccountName: glAccountNumber + ' - ' + glAccountName,
          hasWorkingRows: subRows && subRows.length > 0,
          isHeader: false,
          key: glAccountId + data.year,
          glAccountId: glAccountId,
          year: data.year,
          label: `${isHistorical ? 'HISTORICALS' : 'BUDGET'} ${data.year}`.trim(),
          isFlagged: data.hasUnresolvedComments,
          commentActivity: data.hasComments,

          isCollapsible: isCollapsible || false,
          isCollapsed: localStorage.getItem(`${glAccountId + data.year}collapsed_glrow`),
          icon: !isCollapsible && rowHasBulkCalculation ? 'mdi-information-outline' : '',
          iconHoverText: this.titleIconHoverTexts.globalFormulas,
          rowHasBulkCalculation: rowHasBulkCalculation,

          isLocked: isLocked,

          periodIndex00Amount: aggregateSubRow ? (aggregateSubRow && aggregateSubRow.periodIndex00.amount) : data.periodIndex00.amount || data.periodIndex00,
          periodIndex01Amount: aggregateSubRow ? (aggregateSubRow && aggregateSubRow.periodIndex01.amount) : data.periodIndex01.amount || data.periodIndex01,
          periodIndex02Amount: aggregateSubRow ? (aggregateSubRow && aggregateSubRow.periodIndex02.amount) : data.periodIndex02.amount || data.periodIndex02,
          periodIndex03Amount: aggregateSubRow ? (aggregateSubRow && aggregateSubRow.periodIndex03.amount) : data.periodIndex03.amount || data.periodIndex03,
          periodIndex04Amount: aggregateSubRow ? (aggregateSubRow && aggregateSubRow.periodIndex04.amount) : data.periodIndex04.amount || data.periodIndex04,
          periodIndex05Amount: aggregateSubRow ? (aggregateSubRow && aggregateSubRow.periodIndex05.amount) : data.periodIndex05.amount || data.periodIndex05,
          periodIndex06Amount: aggregateSubRow ? (aggregateSubRow && aggregateSubRow.periodIndex06.amount) : data.periodIndex06.amount || data.periodIndex06,
          periodIndex07Amount: aggregateSubRow ? (aggregateSubRow && aggregateSubRow.periodIndex07.amount) : data.periodIndex07.amount || data.periodIndex07,
          periodIndex08Amount: aggregateSubRow ? (aggregateSubRow && aggregateSubRow.periodIndex08.amount) : data.periodIndex08.amount || data.periodIndex08,
          periodIndex09Amount: aggregateSubRow ? (aggregateSubRow && aggregateSubRow.periodIndex09.amount) : data.periodIndex09.amount || data.periodIndex09,
          periodIndex10Amount: aggregateSubRow ? (aggregateSubRow && aggregateSubRow.periodIndex10.amount) : data.periodIndex10.amount || data.periodIndex10,
          periodIndex11Amount: aggregateSubRow ? (aggregateSubRow && aggregateSubRow.periodIndex11.amount) : data.periodIndex11.amount || data.periodIndex11,
          totalAverage: this.calculateGLAccountTotal(aggregateSubRow || data),

          priorYearTotal: data.priorYearTotal,
          total: data.total,
          varAmount: data.varianceAmount,
          varPercent: data.variancePercentage,
          revCostPerUnit: data.revenueOrCostPerUnit,
          unitCount: data.unitCount,
          sqFt: data.sqFt,
          isHistorical: isHistorical,
          isEditable: isEditable && (!rowHasBulkCalculation || this.hasEditGlobalFormulaPermission) && (!(aggregateSubRow && aggregateSubRow.isLocked) || this.hasEditLockedGLPermission),
          notEditableReason: !isEditable ? this.notEditableReasons.generic : (aggregateSubRow && aggregateSubRow.isLocked ? this.notEditableReasons.locked : (rowHasBulkCalculation && !this.hasEditGlobalFormulaPermission ? this.notEditableReasons.bulkEditable : '')),

          hasHistoricalRecords: data.hasHistoricals,
          isHistoricalRecord: data.year < this.fiscalYear,
          showHistoricalRecords: this.historicalYearsDisplayCount > 0,
          historicalRecordAge: this.fiscalYear - data.year,

          assumptionStatement: isHistorical ? '' : assumptionStatement,
          assumptionActivity: isHistorical ? false : assumptionStatement ? assumptionStatement.length > 0 : false,
          calculations: isHistorical ? '' : calculations,
          calculationSentences: isHistorical ? '' : ((calculations && (calculations[0] && calculations[0].naturalLanguageDescription) || '') + '\n' + (calculations[1] && calculations[1].naturalLanguageDescription || '') || '').trim() || 'No calculations',
          url: (aggregateSubRow && aggregateSubRow.url) || (data.subModels && data.subModels[0] && data.subModels[0].url) || null,
          periodIndex00Url: (data.subModels && data.subModels[0] && data.subModels[0].periodIndex00.url),
          periodIndex01Url: (data.subModels && data.subModels[0] && data.subModels[0].periodIndex01.url),
          periodIndex02Url: (data.subModels && data.subModels[0] && data.subModels[0].periodIndex02.url),
          periodIndex03Url: (data.subModels && data.subModels[0] && data.subModels[0].periodIndex03.url),
          periodIndex04Url: (data.subModels && data.subModels[0] && data.subModels[0].periodIndex04.url),
          periodIndex05Url: (data.subModels && data.subModels[0] && data.subModels[0].periodIndex05.url),
          periodIndex06Url: (data.subModels && data.subModels[0] && data.subModels[0].periodIndex06.url),
          periodIndex07Url: (data.subModels && data.subModels[0] && data.subModels[0].periodIndex07.url),
          periodIndex08Url: (data.subModels && data.subModels[0] && data.subModels[0].periodIndex08.url),
          periodIndex09Url: (data.subModels && data.subModels[0] && data.subModels[0].periodIndex09.url),
          periodIndex10Url: (data.subModels && data.subModels[0] && data.subModels[0].periodIndex10.url),
          periodIndex11Url: (data.subModels && data.subModels[0] && data.subModels[0].periodIndex11.url)
        }
      },
      initCollapsibleGLAccountRow(glAccountId, glAccountNumber, glAccountName, data, isHistorical, isEditable, assumptionStatement, calculations, isLocked) {
        return [this.initGLAccountRow(glAccountId, glAccountNumber, glAccountName, data, isHistorical, isEditable, true, [], assumptionStatement, calculations, isLocked), ...(data.subModels || []).map((row) => { return this.initGLAccountSubRow(glAccountId, glAccountNumber, glAccountName, data, row, isHistorical, isEditable, isLocked) })];
      },
      initSummaryGLAccountRow(glAccountId, data, isHistorical, isEditable, calculations) {
        var rowHasBulkCalculation = (calculations && !calculations.every((c) => c.type != 7));
        return {
          isHeader: false,
          key: glAccountId + data.year,
          glAccountId: glAccountId,
          name: data.name,
          number: data.number,
          label: `${data.name} ${data.number}`.trim(),
          isFlagged: data.hasUnresolvedComments,
          commentActivity: data.hasComments,

          currentYear_budget_perDollar: data.budgets.total,
          priorYear_budget_perDollar: data.budgets.priorYearTotal,
          var_dollars_perDollar: data.budgets.varianceAmount,
          var_percent_perDollar: data.budgets.variancePercentage,
          currentYear_budget_perUnit: data.budgets.amountPerUnit,
          priorYear_budget_perUnit: data.budgets.priorYearAmountPerUnit,
          var_dollars_perUnit: data.budgets.variancePerUnit,
          currentYear_budget_perSqft: data.budgets.amountPerSqft,
          priorYear_budget_perSqft: data.budgets.priorYearAmountPerSqft,
          var_dollars_perSqft: data.budgets.variancePerSqft,

          budgetToActuals_currentYear_budget: data.budgets.total,
          budgetToActuals_priorYear_actuals: data.budgets.priorYearActualsTotal,
          budgetToActuals_variance_dollars: data.budgets.budgetToActualsVarianceDollars,
          budgetToActuals_variance_percent: data.budgets.budgetToActualsVariancePercent,

          budgetToActuals_currentYear_budget_perUnit: data.budgets.budgetToActualsCurrentYearBudgetPerUnit,
          budgetToActuals_priorYear_actuals_perUnit: data.budgets.budgetToActualsPriorYearActualsPerUnit,
          budgetToActuals_perUnit_variance_dollars: data.budgets.budgetToActualsVariancePerUnitDollars,

          budgetToActuals_currentYear_budget_perSqft: data.budgets.budgetToActualsCurrentYearBudgetPerSqft,
          budgetToActuals_priorYear_actuals_perSqft: data.budgets.budgetToActualsPriorYearActualsPerSqft,
          budgetToActuals_perSqft_variance_dollars: data.budgets.budgetToActualsVariancePerSqftDollars,

          budgetToPriorBudget_currentYear_budget: data.budgets.total,
          budgetToPriorBudget_priorYear_budget: data.budgets.priorYearBudgetTotal,
          budgetToPriorBudget_variance_dollars: data.budgets.budgetToPriorBudgetVarianceDollars,
          budgetToPriorBudget_variance_percent: data.budgets.budgetToPriorBudgetVariancePercent,

          budgetToPriorBudget_currentYear_budget_perUnit: data.budgets.budgetToPriorBudgetCurrentYearBudgetPerUnit,
          budgetToPriorBudget_priorYear_budget_perUnit: data.budgets.budgetToPriorBudgetPriorYearBudgetPerUnit,
          budgetToPriorBudget_perDollar_variance_dollars: data.budgets.budgetToPriorBudgetVariancePerUnitDollars,

          budgetToPriorBudget_currentYear_budget_perSqft: data.budgets.budgetToPriorBudgetCurrentYearBudgetPerSqft,
          budgetToPriorBudget_priorYear_budget_perSqft: data.budgets.budgetToPriorBudgetPriorYearBudgetPerSqft,
          budgetToPriorBudget_perSqft_variance_dollars: data.budgets.budgetToPriorBudgetVariancePerSqftDollars,

          budgetToReforecast_currentYear_budget: data.budgets.total,
          budgetToReforecast_priorYear_reforecast: data.budgets.priorYearReforecastTotal,
          budgetToReforecast_variance_dollars: data.budgets.budgetToReforecastVarianceDollars,
          budgetToReforecast_variance_percent: data.budgets.budgetToReforecastVariancePercent,

          budgetToReforecast_currentYear_budget_perUnit: data.budgets.budgetToReforecastCurrentYearBudgetPerUnit,
          budgetToReforecast_priorYear_reforecast_perUnit: data.budgets.budgetToReforecastPriorYearReforecastPerUnit,
          budgetToReforecast_perUnit_variance_dollars: data.budgets.budgetToReforecastVariancePerUnitDollars,

          budgetToReforecast_currentYear_budget_perSqft: data.budgets.budgetToReforecastCurrentYearBudgetPerSqft,
          budgetToReforecast_priorYear_reforecast_perSqft: data.budgets.budgetToReforecastPriorYearReforecastPerSqft,
          budgetToReforecast_perSqft_variance_dollars: data.budgets.budgetToReforecastVariancePerSqftDollars,


          reforecastToActuals_currentYear_reforecast: data.budgets.total,
          reforecastToActuals_currentYear_actuals: data.budgets.currentYearActualsTotal,
          reforecastToActuals_variance_dollars: data.budgets.reforecastToActualsVarianceDollars,
          reforecastToActuals_variance_percent: data.budgets.reforecastToActualsVariancePercent,

          reforecastToActuals_currentYear_reforecast_perUnit: data.budgets.reforecastToActualsCurrentYearReforecastPerUnit,
          reforecastToActuals_currentYear_actuals_perUnit: data.budgets.reforecastToActualsCurrentYearActualsPerUnit,
          reforecastToActuals_perUnit_variance_dollars: data.budgets.reforecastToActualsVariancePerUnitDollars,

          reforecastToActuals_currentYear_reforecast_perSqft: data.budgets.reforecastToActualsCurrentYearReforecastPerSqft,
          reforecastToActuals_currentYear_actuals_perSqft: data.budgets.reforecastToActualsCurrentYearActualsPerSqft,
          reforecastToActuals_perSqft_variance_dollars: data.budgets.reforecastToActualsVariancePerSqftDollars,

          reforecastToBudget_currentYear_reforecast: data.budgets.total,
          reforecastToBudget_currentYear_budget: data.budgets.currentYearBudgetTotal,
          reforecastToBudget_variance_dollars: data.budgets.reforecastToBudgetVarianceDollars,
          reforecastToBudget_variance_percent: data.budgets.reforecastToBudgetVariancePercent,

          reforecastToBudget_currentYear_reforecast_perUnit: data.budgets.reforecastToBudgetCurrentYearReforecastPerUnit,
          reforecastToBudget_currentYear_budget_perUnit: data.budgets.reforecastToBudgetCurrentYearBudgetPerUnit,
          reforecastToBudget_perDollar_variance_dollars: data.budgets.reforecastToBudgetVariancePerUnitDollars,

          reforcastToBudget_currentYear_reforecast_perSqft: data.budgets.reforecastToBudgetCurrentYearReforecastPerSqft,
          reforecastToBudget_currentYear_budget_perSqft: data.budgets.reforecastToBudgetCurrentYearBudgetPerSqft,
          reforecastToBudget_perSqft_variance_dollars: data.budgets.reforecastToBudgetVariancePerSqftDollars,

          assumptionStatement: data.assumptionStatement,
          assumptionActivity: data.assumptionStatement ? data.assumptionStatement.length > 0 : false,
          calculations: calculations,
          calculationSentences: ((calculations && (calculations[0] && calculations[0].naturalLanguageDescription) || '') + '\n' + (calculations[1] && calculations[1].naturalLanguageDescription || '') || '').trim() || 'No calculations',

          priorYearTotal: data.priorYearTotal,
          total: data.total,
          varAmount: data.varianceAmount,
          varPercent: data.variancePercentage,
          revCostPerUnit: data.revenueOrCostPerUnit,
          unitCount: data.unitCount,
          sqFt: data.sqFt,
          isHistorical: isHistorical,

          isEditable: isEditable && (!rowHasBulkCalculation || this.hasEditGlobalFormulaPermission),
          notEditableReason: !isEditable ? this.notEditableReasons.generic : (rowHasBulkCalculation && !this.hasEditGlobalFormulaPermission ? this.notEditableReasons.bulkEditable : ''),
          rowHasBulkCalculation: rowHasBulkCalculation,

          hasHistoricalRecords: data.hasHistoricals,
          isHistoricalRecord: data.year < this.fiscalYear,
          showHistoricalRecords: this.historicalYearsDisplayCount > 0,
          historicalRecordAge: this.fiscalYear - data.year,

          url: isHistorical ? data.url : null
        }
      },
      getPeriodIndexAmount(data, periodIndex) {
        switch (periodIndex) {
          case 0:
            return data.periodIndex00.amount;
          case 1:
            return data.periodIndex01.amount;
          case 2:
            return data.periodIndex02.amount;
          case 3:
            return data.periodIndex03.amount;
          case 4:
            return data.periodIndex04.amount;
          case 5:
            return data.periodIndex05.amount;
          case 6:
            return data.periodIndex06.amount;
          case 7:
            return data.periodIndex07.amount;
          case 8:
            return data.periodIndex08.amount;
          case 9:
            return data.periodIndex09.amount;
          case 10:
            return data.periodIndex10.amount;
          case 11:
            return data.periodIndex11.amount;
          default:
            break;
        }
      },
      gridActionClicked(action, params) {
        switch (action.name) {
          case 'open-assumptions':
            if (this.isLockedOut(params.data)) {
              this.displayLockedMessage();
              return;
            }
            this.editingGLAccountId = params.data.glAccountId;
            this.openAssumptionsDialog(params.data);
            break;
          case 'add-row':
            if (this.readonly) {
              this.displayDeniedMessage();
              return;
            }
            if (this.isLockedOut(params.data)) {
              this.displayLockedMessage();
              return;
            }
            this.promptAddWorkingRow(params.data.glAccountId, params.data.hasWorkingRows);
            break;
          case 'delete-rows':
            if (this.readonly) {
              this.displayDeniedMessage();
              return;
            }
            if (this.isLockedOut(params.data)) {
              this.displayLockedMessage();
              return;
            }
            this.showDeleteRowsDialog(params.data.glAccountId);
            break;
          case 'add-comment':
            this.initializeCommentService(this.budgetIterationId, params.data.glAccountId, this.readonly)
            this.openCommentsDialog()
            break;
          case 'clear-reset':
            if (this.readonly) {
              this.displayDeniedMessage();
              return;
            }
            if (this.isLockedOut(params.data)) {
              this.displayLockedMessage();
              return;
            }
            this.openClearResetDialog(params.data.glAccountId);
            break;
          case 'toggle-gl-locked':
            if (this.hasLockUnlockGLPermission) {
              this.toggleIsLocked(params);
              this.$eventHub.$emit('showAlertNotification', `GL Account ${params.data.isLocked ? "unlocked" : "locked"}`);
            }
            else {
              this.displayLockedLockUnlockMessage();
            }
        }
      },
      updateIsFlagged(isFlagged) {
        this.glAccountData.forEach((gl) => {
          if (gl.isHeader && gl.glAccountId == this.commentService.glAccountId) {
            gl.isFlagged = isFlagged;
          }
        });
      },
      updateCommentActivity(activityExists) {
        this.glAccountData.forEach((gl) => {
          if (gl.isHeader && gl.glAccountId == this.commentService.glAccountId) {
            gl.commentActivity = activityExists;
          }
        });
      },
      toggleUnitDetailsColumn() {
        this.resizeRows();
      },
      toggleSqftDetailsColumn() {
        this.resizeRows();
      },
      toggleCalculationsColumn() {
        this.resizeRows();
      },
      toggleAssumptionsColumn() {
        this.resizeRows();
      },
      selectAll() {
        this.anyCheckSelected = this.allCheckSelected = !this.allCheckSelected;
        this.glAccountData = this.glAccountData.map((data) => {
          data.isSelected = this.allCheckSelected;
          if (data.isHeader) {
            this.selectedGLAccounts[data.glAccountId] = data;
          }
          return data;
        })
      },
      onRowSelectedChanged() {
        // check or uncheck the 'select all' button based on whether every header row is now selected
        // recolor bulk calculator icon if all gl groups are unchecked
        var anyChecked = false;
        var anyNotChecked = false;
        this.anySelectedHasBulkCalcs = false;
        this.anySelectedIsLocked = false;
        this.glAccountData.filter((data) => { return data.isHeader }).forEach((data) => {
          this.selectedGLAccounts[data.glAccountId] = data;
          if (data.isSelected) {
            anyChecked = true;
            if (!this.hasEditGlobalFormulaPermission && data.rowHasBulkCalculation) {
              this.anySelectedHasBulkCalcs = true;
            }
            if (!this.hasEditLockedGLPermission && data.isLocked) {
              this.anySelectedIsLocked = true;
            }
          } else { anyNotChecked = true; }
        })

        this.allCheckSelected = anyChecked && !anyNotChecked;
        this.anyCheckSelected = anyChecked;
      },
      cellClass(columnIndex) {
        return (columnIndex % 2 === 0) ? '' : 'shaded';
      },
      isHeaderCell(params) {
        return params.data.isHeader;
      },
     async toggleIsLocked(params) {
        if (params.data) {
          params = params.data;
        }
  
  if (!this.hasLockUnlockGLPermission) {
    this.displayLockedLockUnlockMessage();
    return;
  }

        let newLockedSetting = !params.isLocked;
  
  try {
     await budgetGLAccountService.setLocked(this.budgetIterationId, params.glAccountId, newLockedSetting);
    
    this.glAccountData.forEach((glRow) => {
      if (glRow.glAccountId === params.glAccountId) {
        if (glRow.isHeader || glRow.isGLMainRow) {
          glRow.isLocked = newLockedSetting;
        }
        
        if (glRow.isWorkingRow) {
          glRow.parentGLIsLocked = newLockedSetting;
          
          const effectiveIsLocked = newLockedSetting || glRow.isLocked;
          
          glRow.isEditable = (!effectiveIsLocked || this.hasEditLockedGLPermission) &&
                             (!glRow.rowHasBulkCalculation || this.hasEditGlobalFormulaPermission);
          
          glRow.notEditableReason = effectiveIsLocked && !this.hasEditLockedGLPermission ?
            this.notEditableReasons.locked : 
            (glRow.rowHasBulkCalculation && !this.hasEditGlobalFormulaPermission ? 
             this.notEditableReasons.bulkEditable : '');
        }
      }
    });
    
           this.glAccountDataGridOptions.api.refreshCells({ force: true });
    
     this.$eventHub.$emit('showAlertNotification', 
      `GL Account ${newLockedSetting ? "locked" : "unlocked"}`);
      
  } catch (error) {
    console.error('Error toggling GL Account lock:', error);
    this.$eventHub.$emit('showAlertNotification', 'Error updating lock status');
  }
},
      isLockedOut(params) {
        return (params.isLocked || params.data && params.data.isLocked) && !this.hasEditLockedGLPermission;
      },
      displayLockedMessage() {
        this.$eventHub.$emit('showAlertNotification', this.notEditableReasons.locked);
      },
      displayLockedLockUnlockMessage() {
        this.$eventHub.$emit('showAlertNotification', this.notEditableReasons.cannotLockUnlock);
      },
      isTitleCellEditable(params) {
      const effectivelyLocked = params.data.parentGLIsLocked || params.data.isLocked;
        return (params.data.isWorkingRow && (!params.data.rowHasBulkCalculation || this.hasEditGlobalFormulaPermission) && (!effectivelyLocked || this.hasEditLockedGLPermission));
     },
      isCellEditable(params) {
        if (this.readonly) {
    return false;
  }
  
  const isWorkingRowLocked = params.data.isWorkingRow && 
                              (params.data.isLocked || params.data.parentGLIsLocked);
  
  const isGLRowLocked = params.data.isGLMainRow && params.data.isLocked;
  
  if ((isWorkingRowLocked || isGLRowLocked) && !this.hasEditLockedGLPermission) {
    return false;
  }
  
        if (params.data.isEditable && !this.isLockedOut(params)) {
          switch (params.colDef.field) {
            case 'periodIndex00Amount':
            case 'periodIndex01Amount':
            case 'periodIndex02Amount':
            case 'periodIndex03Amount':
            case 'periodIndex04Amount':
            case 'periodIndex05Amount':
            case 'periodIndex06Amount':
            case 'periodIndex07Amount':
            case 'periodIndex08Amount':
            case 'periodIndex09Amount':
            case 'periodIndex10Amount':
            case 'periodIndex11Amount':
              return true;
            default:
              return false;
          }
        }
        return false;
      },
      cellEditingStarted(ev) {
        this.lastCellEditedEvent = ev;
        this.changeIsEditing(true);
      },
      cellEditingStopped() {
        setTimeout(() => {
          this.changeIsEditing(false);
          setTimeout(this.finalizePaste, 200)
        }, 200);
      },
      onCellClicked(ev) {
          if (!ev.data.isEditable) {
             if (ev.data.notEditableReason) {
          if (ev.data.notEditableReason == this.notEditableReasons.bulkEditable && this.hasEditGlobalFormulaPermission) {
            return;
          }
          if (ev.data.notEditableReason == this.notEditableReasons.locked && this.hasEditLockedGLPermission) {
            return;
          }
          this.toggleShowNotEditable(ev.data.notEditableReason);
           }
        return;
        }
  
  if (ev.data.isWorkingRow) {
    const effectivelyLocked = ev.data.parentGLIsLocked || ev.data.isLocked;
    if (effectivelyLocked && !this.hasEditLockedGLPermission) {
      this.toggleShowNotEditable(this.notEditableReasons.locked);
    }
        }
      },
      async handlePaste(ev) {
        if (!ev.target.classList.contains('ag-text-field-input')) {
          // paste was not into the grid, ignore
          return;
        }

        this.pastedData = await this.getClipboardData();
        setTimeout(() => { document.activeElement.blur() }); // immediately defocus, will trigger cellEditingStopped
      },
      async getClipboardData() {
        var clipboardData = await navigator.clipboard.readText();
        if (!clipboardData || !clipboardData.split) {
          return; // copy data was in an invalid format, ignore
        }

        var cellData = clipboardData
          .trim()
          .split('\t')
          .filter((cell) => { return !isNaN(cell); })
          .map((cell) => { return +cell });

        return cellData;
      },
      finalizePaste() {
        if (!this.pastedData) {
          return; // no data to paste
        }
        if (!this.lastCellEditedEvent) {
          return; // paste was not into grid cell, ignore
        }

        // Find index of column pasted into
        var allColumns = this.lastCellEditedEvent.node.columnController.getAllGridColumns();
        var startColId = this.lastCellEditedEvent.colDef.field;
        var startIndex = allColumns.findIndex((col) => { return col.colId == startColId });

        if (startIndex === -1) {
          return; // couldn't find column
        }

        // Iterate through pasted data
        for (var i = startIndex; i < startIndex + this.pastedData.length; i++) {
          // Cannot copy past 12th column
          if (i < 13) {
            var colId = allColumns[i].colId;
            this.lastCellEditedEvent.node.setDataValue(colId, this.pastedData[i - startIndex]);
          }
        }

        // clear data
        this.pastedData = null;
      },
      async onCellValuesChanged(params) {
        let t = this;

        if (!params.node.data.isEditable || t.lockGrid) {
          return;
        }

        let result = null;
        if (!params.data.isWorkingRow) {
          // main GL account
          result = await t.handleGLAccountUpdated(params);
        } else {
          // working row
          result = await t.handleGLAccountWorkingRowUpdated(params);
        }

        // update timestamp
        if (result && result.data && result.data.lastModifiedTimestamp) {
          t.budgetIterationLastModifiedTimestamp = result.data.lastModifiedTimestamp;
        }

        // complete pending navigation or refresh data
        if (t.pendingNavigationCallback) {
          t.pendingNavigationCallback(true);
          t.pendingNavigationCallback = null;
        }
        else {
          // refresh group totals
          await t.refreshGroupTotals();
        }
      },
      async handleGLAccountUpdated(params) {
        this.lockGrid = true;
        
        // recalculate total
        params.data.totalAverage = this.calculateGLAccountTotal(params.data);

        // update detail view
        this.glAccountDataGridOptions.api.applyTransactionAsync({ update: [params.data] });

        this.lockGrid = false;

        // post updates to the backend
        let result = await budgetGLAccountService.updateBudgetGLAccounts(this.budgetIterationId,
          [
            {
              glAccountId: params.data.glAccountId,
              periodIndex00: params.data.periodIndex00Amount,
              periodIndex01: params.data.periodIndex01Amount,
              periodIndex02: params.data.periodIndex02Amount,
              periodIndex03: params.data.periodIndex03Amount,
              periodIndex04: params.data.periodIndex04Amount,
              periodIndex05: params.data.periodIndex05Amount,
              periodIndex06: params.data.periodIndex06Amount,
              periodIndex07: params.data.periodIndex07Amount,
              periodIndex08: params.data.periodIndex08Amount,
              periodIndex09: params.data.periodIndex09Amount,
              periodIndex10: params.data.periodIndex10Amount,
              periodIndex11: params.data.periodIndex11Amount,
            }
          ], this.budgetIterationLastModifiedTimestamp);

        return result;
      },
      async handleGLAccountWorkingRowUpdated(params) {
        this.lockGrid = true;

        let rowsToUpdate = [];

        // recalculate working row total
        params.data.totalAverage = this.calculateGLAccountTotal(params.data);
        rowsToUpdate.push(params.data);

        // find sibling working rows and the main gl account row
        let glMainRowIndex = -1;
          let glWorkingRows = [];
          this.pagedGLAccountData.forEach((glRow, index) => {
          if (glRow.isWorkingRow && glRow.glAccountId == params.data.glAccountId) {
            glWorkingRows.push(glRow);
          } else if (glRow.isGLMainRow && !glRow.isHistorical && glRow.glAccountId == params.data.glAccountId) {
            glMainRowIndex = index;
          }
        });

        if (glMainRowIndex > -1 && glWorkingRows.length > 0) {
          let mainRowNodeData = (params.api.getRowNode(glMainRowIndex) || { data: null }).data;
          if (mainRowNodeData == null) return;
          let aggregateWorkingRow = this.aggregateSubRows(glWorkingRows);

          // recalculate main row period indices
          mainRowNodeData.periodIndex00Amount = aggregateWorkingRow.periodIndex00.amount;
          mainRowNodeData.periodIndex01Amount = aggregateWorkingRow.periodIndex01.amount;
          mainRowNodeData.periodIndex02Amount = aggregateWorkingRow.periodIndex02.amount;
          mainRowNodeData.periodIndex03Amount = aggregateWorkingRow.periodIndex03.amount;
          mainRowNodeData.periodIndex04Amount = aggregateWorkingRow.periodIndex04.amount;
          mainRowNodeData.periodIndex05Amount = aggregateWorkingRow.periodIndex05.amount;
          mainRowNodeData.periodIndex06Amount = aggregateWorkingRow.periodIndex06.amount;
          mainRowNodeData.periodIndex07Amount = aggregateWorkingRow.periodIndex07.amount;
          mainRowNodeData.periodIndex08Amount = aggregateWorkingRow.periodIndex08.amount;
          mainRowNodeData.periodIndex09Amount = aggregateWorkingRow.periodIndex09.amount;
          mainRowNodeData.periodIndex10Amount = aggregateWorkingRow.periodIndex10.amount;
          mainRowNodeData.periodIndex11Amount = aggregateWorkingRow.periodIndex11.amount;

          // recalculate main row total
          mainRowNodeData.totalAverage = this.calculateGLAccountTotal(mainRowNodeData)
          rowsToUpdate.push(mainRowNodeData);
        }

        // update detail view
        this.glAccountDataGridOptions.api.applyTransactionAsync({ update: rowsToUpdate });
        
        this.lockGrid = false;

        // post updates to the backend
        let result = await budgetGLAccountService.updateWorkingRow(params.data.budgetGLAccountWorkingRowId,
          {
            name: params.data.label,
            periodIndex00: params.data.periodIndex00Amount,
            periodIndex01: params.data.periodIndex01Amount,
            periodIndex02: params.data.periodIndex02Amount,
            periodIndex03: params.data.periodIndex03Amount,
            periodIndex04: params.data.periodIndex04Amount,
            periodIndex05: params.data.periodIndex05Amount,
            periodIndex06: params.data.periodIndex06Amount,
            periodIndex07: params.data.periodIndex07Amount,
            periodIndex08: params.data.periodIndex08Amount,
            periodIndex09: params.data.periodIndex09Amount,
            periodIndex10: params.data.periodIndex10Amount,
            periodIndex11: params.data.periodIndex11Amount,
          }
        );

        return result;
      },
      // combine an array of subrows into one row
      aggregateSubRows(subRows) {
        let blankRow = {
          periodIndex00: { amount: 0 },
          periodIndex01: { amount: 0 },
          periodIndex02: { amount: 0 },
          periodIndex03: { amount: 0 },
          periodIndex04: { amount: 0 },
          periodIndex05: { amount: 0 },
          periodIndex06: { amount: 0 },
          periodIndex07: { amount: 0 },
          periodIndex08: { amount: 0 },
          periodIndex09: { amount: 0 },
          periodIndex10: { amount: 0 },
          periodIndex11: { amount: 0 }
        }
        return (subRows || [blankRow]).reduce((accumulator, currentValue) => {
          return {
            url: accumulator && accumulator.url || currentValue.url,
            periodIndex00: { amount: ((accumulator && accumulator.periodIndex00 && accumulator.periodIndex00.amount) || 0) + (currentValue.periodIndex00Amount || (currentValue.periodIndex00 && currentValue.periodIndex00.amount) || 0) },
            periodIndex01: { amount: ((accumulator && accumulator.periodIndex01 && accumulator.periodIndex01.amount) || 0) + (currentValue.periodIndex01Amount || (currentValue.periodIndex01 && currentValue.periodIndex01.amount) || 0) },
            periodIndex02: { amount: ((accumulator && accumulator.periodIndex02 && accumulator.periodIndex02.amount) || 0) + (currentValue.periodIndex02Amount || (currentValue.periodIndex02 && currentValue.periodIndex02.amount) || 0) },
            periodIndex03: { amount: ((accumulator && accumulator.periodIndex03 && accumulator.periodIndex03.amount) || 0) + (currentValue.periodIndex03Amount || (currentValue.periodIndex03 && currentValue.periodIndex03.amount) || 0) },
            periodIndex04: { amount: ((accumulator && accumulator.periodIndex04 && accumulator.periodIndex04.amount) || 0) + (currentValue.periodIndex04Amount || (currentValue.periodIndex04 && currentValue.periodIndex04.amount) || 0) },
            periodIndex05: { amount: ((accumulator && accumulator.periodIndex05 && accumulator.periodIndex05.amount) || 0) + (currentValue.periodIndex05Amount || (currentValue.periodIndex05 && currentValue.periodIndex05.amount) || 0) },
            periodIndex06: { amount: ((accumulator && accumulator.periodIndex06 && accumulator.periodIndex06.amount) || 0) + (currentValue.periodIndex06Amount || (currentValue.periodIndex06 && currentValue.periodIndex06.amount) || 0) },
            periodIndex07: { amount: ((accumulator && accumulator.periodIndex07 && accumulator.periodIndex07.amount) || 0) + (currentValue.periodIndex07Amount || (currentValue.periodIndex07 && currentValue.periodIndex07.amount) || 0) },
            periodIndex08: { amount: ((accumulator && accumulator.periodIndex08 && accumulator.periodIndex08.amount) || 0) + (currentValue.periodIndex08Amount || (currentValue.periodIndex08 && currentValue.periodIndex08.amount) || 0) },
            periodIndex09: { amount: ((accumulator && accumulator.periodIndex09 && accumulator.periodIndex09.amount) || 0) + (currentValue.periodIndex09Amount || (currentValue.periodIndex09 && currentValue.periodIndex09.amount) || 0) },
            periodIndex10: { amount: ((accumulator && accumulator.periodIndex10 && accumulator.periodIndex10.amount) || 0) + (currentValue.periodIndex10Amount || (currentValue.periodIndex10 && currentValue.periodIndex10.amount) || 0) },
            periodIndex11: { amount: ((accumulator && accumulator.periodIndex11 && accumulator.periodIndex11.amount) || 0) + (currentValue.periodIndex11Amount || (currentValue.periodIndex11 && currentValue.periodIndex11.amount) || 0) },
            isLocked: (accumulator && accumulator.isLocked) || (currentValue && currentValue.isLocked)
          }
        }, blankRow);
      },
      // sum up the indices of a single row
      calculateGLAccountTotal(data) {
        let runningTotal =
          this.parseCurrency(data.periodIndex00Amount || (data.periodIndex00 && data.periodIndex00.amount) || data.periodIndex00 || 0) +
          this.parseCurrency(data.periodIndex01Amount || (data.periodIndex01 && data.periodIndex01.amount) || data.periodIndex01 || 0) +
          this.parseCurrency(data.periodIndex02Amount || (data.periodIndex02 && data.periodIndex02.amount) || data.periodIndex02 || 0) +
          this.parseCurrency(data.periodIndex03Amount || (data.periodIndex03 && data.periodIndex03.amount) || data.periodIndex03 || 0) +
          this.parseCurrency(data.periodIndex04Amount || (data.periodIndex04 && data.periodIndex04.amount) || data.periodIndex04 || 0) +
          this.parseCurrency(data.periodIndex05Amount || (data.periodIndex05 && data.periodIndex05.amount) || data.periodIndex05 || 0) +
          this.parseCurrency(data.periodIndex06Amount || (data.periodIndex06 && data.periodIndex06.amount) || data.periodIndex06 || 0) +
          this.parseCurrency(data.periodIndex07Amount || (data.periodIndex07 && data.periodIndex07.amount) || data.periodIndex07 || 0) +
          this.parseCurrency(data.periodIndex08Amount || (data.periodIndex08 && data.periodIndex08.amount) || data.periodIndex08 || 0) +
          this.parseCurrency(data.periodIndex09Amount || (data.periodIndex09 && data.periodIndex09.amount) || data.periodIndex09 || 0) +
          this.parseCurrency(data.periodIndex10Amount || (data.periodIndex10 && data.periodIndex10.amount) || data.periodIndex10 || 0) +
          this.parseCurrency(data.periodIndex11Amount || (data.periodIndex11 && data.periodIndex11.amount) || data.periodIndex11 || 0);
        return this.parseCurrency(runningTotal);
      },
      getFutureHistoricalCellIndicator(data, periodIndex) {
        return data.isGroupTotal && (data.periodIndexIsPopulatedFromFutureHistoricalSettings || [])[periodIndex] ? '*' : '';
      },
      formatBudgetValueWithHistoricalCellIndicator(params) {
        return this.getFutureHistoricalCellIndicator(params.data, +params.column.colId.replace('periodIndex', '').replace('Amount', '')) + this.formatBudgetValueDollar(params);
      },
      parseBudgetValue(params) {
        return this.parseCurrency(params.newValue, false);
      },
      formatBudgetValueDollar(params) {
        return this.formatCurrency(params.value, true, true);
      },
      formatBudgetValueDollarWithCents(params) {
        return this.formatCurrency(params.value, false, true);
      },
      formatBudgetValueRoundedPercent(params) {
        return this.formatPercentage(params.value, true, true);
      },
      shouldRowsBeFiltered() {
        return true;
      },
      shouldShowRow(node) {
        return node.data.isHeader == true || ((node.data.isCollapsible || !node.data.isCollapsed) && (node.data.isHistoricalRecord == false ||
          (this.showHistoricalData && node.data.historicalRecordAge <= this.historicalYearsDisplayCount)));
      },
      async historicalDataYearsDisplayedChanged(yearsDisplayed) {
        if (yearsDisplayed && !this.historicalYearsDisplayCount) {
          this.historicalYearsDisplayCount = yearsDisplayed;
          await this.loadBudgetGLAccounts();
        }
        this.historicalYearsDisplayCount = yearsDisplayed;
        this.refreshDataFilter();
      },
      refreshDataFilter() {
        this.showHistoricalData = this.historicalYearsDisplayCount > 0;
        this.groupDataGridOptions.api.onFilterChanged();
        this.glAccountDataGridOptions.api.onFilterChanged();
      },
      selectedViewOptionChanged() {
        // toggle summary/detail
        this.selectedViewOption = +(Boolean(!this.selectedViewOption));

        this.resizeRows();
      },
      resizeRows() {
        setTimeout(() => {
          // toggle dollar detail columns
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToPriorBudget_currentYear_budget', this.showPriorYrBudgetDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToPriorBudget_priorYear_budget', this.showPriorYrBudgetDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToPriorBudget_variance_dollars', this.showPriorYrBudgetDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToPriorBudget_variance_percent', this.showPriorYrBudgetDetails);

          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToReforecast_currentYear_budget', this.showPriorYrReforecastDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToReforecast_priorYear_reforecast', this.showPriorYrReforecastDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToReforecast_variance_dollars', this.showPriorYrReforecastDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToReforecast_variance_percent', this.showPriorYrReforecastDetails);

          // toggle unit detail columns
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToActuals_currentYear_budget_perUnit', this.showUnitDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToActuals_priorYear_actuals_perUnit', this.showUnitDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToActuals_perUnit_variance_dollars', this.showUnitDetails);

          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToPriorBudget_currentYear_budget_perUnit', this.showUnitDetails && this.showPriorYrBudgetDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToPriorBudget_priorYear_budget_perUnit', this.showUnitDetails && this.showPriorYrBudgetDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToPriorBudget_perDollar_variance_dollars', this.showUnitDetails && this.showPriorYrBudgetDetails);

          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToReforecast_currentYear_budget_perUnit', this.showUnitDetails && this.showPriorYrReforecastDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToReforecast_priorYear_reforecast_perUnit', this.showUnitDetails && this.showPriorYrReforecastDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToReforecast_perUnit_variance_dollars', this.showUnitDetails && this.showPriorYrReforecastDetails);

          // toggle sqft detail columns
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToActuals_currentYear_budget_perSqft', this.showSqftDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToActuals_priorYear_actuals_perSqft', this.showSqftDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToActuals_perSqft_variance_dollars', this.showSqftDetails);

          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToPriorBudget_currentYear_budget_perSqft', this.showSqftDetails && this.showPriorYrBudgetDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToPriorBudget_priorYear_budget_perSqft', this.showSqftDetails && this.showPriorYrBudgetDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToPriorBudget_perSqft_variance_dollars', this.showSqftDetails && this.showPriorYrBudgetDetails);

          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToReforecast_currentYear_budget_perSqft', this.showSqftDetails && this.showPriorYrReforecastDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToReforecast_priorYear_reforecast_perSqft', this.showSqftDetails && this.showPriorYrReforecastDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('budgetToReforecast_perSqft_variance_dollars', this.showSqftDetails && this.showPriorYrReforecastDetails);

          //// toggle reforecast detail columns
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforecastToActuals_currentYear_reforecast', true);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforecastToActuals_currentYear_actuals', true);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforecastToActuals_variance_dollars', true);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforecastToActuals_variance_percent', true);

          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforecastToActuals_currentYear_reforecast_perUnit', this.showUnitDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforecastToActuals_currentYear_actuals_perUnit', this.showUnitDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforecastToActuals_perUnit_variance_dollars', this.showUnitDetails);

          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforecastToActuals_currentYear_reforecast_perSqft', this.showSqftDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforecastToActuals_currentYear_actuals_perSqft', this.showSqftDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforecastToActuals_perSqft_variance_dollars', this.showSqftDetails);

          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforecastToBudget_currentYear_reforecast', this.showCurrentYrBudgetDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforecastToBudget_currentYear_budget', this.showCurrentYrBudgetDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforecastToBudget_variance_dollars', this.showCurrentYrBudgetDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforecastToBudget_variance_percent', this.showCurrentYrBudgetDetails);

          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforecastToBudget_currentYear_reforecast_perUnit', this.showCurrentYrBudgetDetails && this.showUnitDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforecastToBudget_currentYear_budget_perUnit', this.showCurrentYrBudgetDetails && this.showUnitDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforecastToBudget_perDollar_variance_dollars', this.showCurrentYrBudgetDetails && this.showUnitDetails);

          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforcastToBudget_currentYear_reforecast_perSqft', this.showCurrentYrBudgetDetails && this.showSqftDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforecastToBudget_currentYear_budget_perSqft', this.showCurrentYrBudgetDetails && this.showSqftDetails);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('reforecastToBudget_perSqft_variance_dollars', this.showCurrentYrBudgetDetails && this.showSqftDetails);


          // toggle calculation/assumptions columns
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('calculationSentences', this.showCalculations);
          this.groupDataGridOptions.columnApi.setColumnVisible('calculationSentences', this.showCalculations);
          this.groupSummaryDataGridOptions.columnApi.setColumnVisible('assumptionStatement', this.showAssumptions);
          this.groupDataGridOptions.columnApi.setColumnVisible('assumptionStatement', this.showAssumptions);

          // reset row heights
          this.initSummaryColumns();
          this.groupSummaryDataGridOptions.api.resetRowHeights();
          this.groupSummaryDataGridOptions.api.sizeColumnsToFit();
          this.glAccountDataGridOptions.api.resetRowHeights();
        }, 100)
      },
      openRowCalculator(data) {
        if (this.isLockedOut(data)) {
          this.displayLockedMessage();
          return;
        }
        if (data.notEditableReason) {
          if (data.notEditableReason == this.notEditableReasons.bulkEditable && this.hasEditGlobalFormulaPermission) {
            return;
          }
          this.toggleShowNotEditable(data.notEditableReason);
          return;
        }
        data.isSelected = true;
        this.selectedGLAccounts = {};
        this.selectedGLAccounts[data.glAccountId] = data;
        this.selectedGLAccounts[data.glAccountId].workingRowIds = data.budgetGLAccountWorkingRowId ? [data.budgetGLAccountWorkingRowId] : [];
        this.openCreateCalculationDialog();
      },
      openBulkCalculator() {
        this.onRowSelectedChanged();
        if (this.anySelectedIsLocked) {
          this.toggleShowNotEditable(this.notEditableReasons.locked);
          return;
        }
        if (this.anySelectedHasBulkCalcs) {
          this.toggleShowNotEditable(this.notEditableReasons.bulkEditable);
          return;
        }
        this.openCreateCalculationDialog();
      },
      openClearResetDialog(glAccountId) {
        this.clearingGLAccountId = glAccountId;
        this.showClearResetDialog = true
      },
      closeClearResetDialog() {
        this.clearingGLAccountId = null;
        this.showClearResetDialog = false
      },
      async clearResetConfirmed() {
        this.changeLoading(true);
        await budgetGLAccountService.reset(this.budgetIterationId, this.clearingGLAccountId);
        this.closeClearResetDialog();
        location.reload();
      },
      toggleShowNotEditable(reason) {
        if (reason) {
          this.notEditableReason = reason;
          this.showNotEditable = true;
        }
      },
      closeNotEditable() {
        this.showNotEditable = false;
      },
      // wrap service for this budget+gl account
      initializeCommentService(budgetIterationId, glAccountId, readonly) {
        this.commentService = {
          glAccountId: glAccountId,
          budgetIterationId: budgetIterationId,
          getComments: () => budgetGLAccountService.getComments(budgetIterationId, glAccountId),
          createComment: async (note, isFlag, repliesToNoteId) => {
            if (readonly) {
              this.displayDeniedMessage();
              return;
            }
            return await budgetGLAccountService.createComment(note, isFlag, repliesToNoteId, budgetIterationId, glAccountId)
          },
          resolveComment: async (budgetGLAccountNoteId, note) => {
            if (readonly) {
              this.displayDeniedMessage();
              return;
            }
            return await budgetGLAccountService.resolveComment(budgetGLAccountNoteId, note)
          },
          editComment: async (commentId, commentText) => {
            if (readonly) {
              this.displayDeniedMessage();
              return;
            }
            return await budgetGLAccountService.editComment(commentId, commentText);
          },
          deleteComment: async (commentId) => {
            if (readonly) {
              this.displayDeniedMessage();
              return;
            }
            return await budgetGLAccountService.deleteComment(commentId);
          }
        }
      },
      openCommentsDialog() {
        this.isCommentsDialogOpen = true
      },
      closeCommentsDialog() {
        this.isCommentsDialogOpen = false
      },
      openAddWorkingRowsDialog() {
        this.isAddWorkingRowsDialogOpen = true;
      },
      closeAddWorkingRowsDialog() {
        this.isAddWorkingRowsDialogOpen = false;
      },
      confirmAddWorkingRowsDialog() {
        this.closeAddWorkingRowsDialog();
        this.addWorkingRow(this.addingRowsForGLAccountId);
      },
      promptAddWorkingRow(glAccountId, hasWorkingRows) {
        if (hasWorkingRows) {
          this.addWorkingRow(glAccountId);
        }
        else {
          this.addingRowsForGLAccountId = glAccountId;
          this.openAddWorkingRowsDialog();
        }
      },
      addWorkingRow(glAccountId) {
        budgetGLAccountService.createWorkingRow(this.budgetIterationId, glAccountId).then(() => {
          this.loadBudgetGLAccounts();
        })
      },
      showDeleteRowsDialog(glAccountId) {
        this.deletingGLAccountRowsOptions = this.glAccountData
          .filter((row) => { return row.isWorkingRow && row.glAccountId == glAccountId && !row.isLocked && !row.parentGLIsLocked; })
          .map((row) => { return { text: row.label, value: row.key }; });

        this.isDeleteRowsDialogOpen = true;
      },
      hideDeleteRowsDialog() {
        this.deletingGLAccountRowsOptions = [];
        this.isDeleteRowsDialogOpen = false;
      },
      async confirmDeleteWorkingRows(e) {
        const lockedRows = e.selectedOptions.filter(option => {
        const row = this.glAccountData.find(r => r.key === option.value);
        return row && row.isLocked;
    });
    
    if (lockedRows.length > 0 && !this.hasEditLockedGLPermission) {
        this.$eventHub.$emit('showAlertNotification', 
            'Cannot delete locked working rows without Edit Locked GL Accounts permission');
        return;
    }
    
        var result = await budgetGLAccountService.deleteWorkingRows(e.selectedOptions || []);

        if (result) {
          this.$eventHub.$emit('showAlertNotification', `${e.selectedOptions.length} working row(s) deleted!`);
          this.loadBudgetGLAccounts();
        }
      },
      openCreateCalculationDialog() {
        if (this.readonly) {
          this.displayDeniedMessage();
          return;
        }
        this.isCreateCalculationDialogOpen = true;
      },
      closeCreateCalculationDialog() {
        this.isCreateCalculationDialogOpen = false;
      },
      openAssumptionsDialog(params) {
        this.isAssumptionsDialogOpen = true;
        this.assumptionStatement = params.assumptionStatement || '';
        this.assumptionCalculations = (params.calculations || []).slice(0, 2);
      },
      closeAssumptionsDialog() {
        this.editingGLAccountId = null;
        this.assumptionStatement = null;
        this.isAssumptionsDialogOpen = false;
      },
      async updateAssumptionStatement(newAssumptionStatement) {
        await budgetGLAccountService.updateAssumptionStatement(this.budgetIterationId, this.editingGLAccountId, newAssumptionStatement);

        let headerRow = this.getGLAccountHeaderRowNode(this.editingGLAccountId);
        let detailRow = this.getGLAccountRowNode(this.editingGLAccountId);
        let summaryRow = this.getSummaryGLAccountRowNode(this.editingGLAccountId);

        if (!headerRow || !detailRow || !summaryRow)
        {
          console.debug(`Missed row node for GLID ${this.editingGLAccountId}:`, headerRow, detailRow, summaryRow, [this.pagedGLAccountData, this.glAccountData]);

          this.closeAssumptionsDialog();

          var _currentPage = this.currentPage;
          await this.loadBudgetGLAccounts();

          setTimeout(() => {
            this.currentPage = _currentPage;
            this.setGLPageSize();
          });
          return;
        }

        headerRow.data.assumptionStatement = newAssumptionStatement;
        headerRow.data.assumptionActivity = newAssumptionStatement && newAssumptionStatement.length;
        detailRow.data.assumptionStatement = newAssumptionStatement;
        summaryRow.data.assumptionStatement = newAssumptionStatement;

        this.glAccountDataGridOptions.api.applyTransactionAsync({ update: [headerRow.data, detailRow.data] });
        this.groupSummaryDataGridOptions.api.applyTransactionAsync({ update: [summaryRow.data] });

        this.closeAssumptionsDialog();
      },
      handleScroll() {

        if ((document.querySelector('.gl-totals-view').getBoundingClientRect().bottom || 0) < 100) {
          this.pinHandler = this.pinHandler || _debounce(this.pinHeaders, 300);
          this.pinHandler();
        }
        else {
          this.unpinHandler = this.unpinHandler || _debounce(this.unpinHeaders, 300);
          this.unpinHandler();
        }
      },
      pinHeaders() {
        var floatingHeader = this.$el.querySelector('div[ref="headerRoot"]');
        if (floatingHeader) {
          floatingHeader.style.position = 'fixed';
          floatingHeader.style.transition = 'top 1.75s ease-in 0.4'
          floatingHeader.style.zIndex = 5;
          floatingHeader.style.top = '50px';
        }
      },
      unpinHeaders() {
        var floatingHeader = this.$el.querySelector('div[ref="headerRoot"]');
        if (floatingHeader) {
          floatingHeader.style.position = 'inherit';
          floatingHeader.style.top = 'inherit';
        }
      },
      getGLAccountHeaderRowNode(glAccountId) {
        let i = 0;
        for (i = 0; i < this.pagedGLAccountData.length; i++) {
          let row = this.pagedGLAccountData[i];
         if (row.isHeader && row.glAccountId == glAccountId) {
           break;
         }
       }
       
       return this.glAccountDataGridOptions.api.getRowNode(i);
      },
      getGLAccountRowNode(glAccountId) {
       let i = 0;
        for (i = 0; i < this.pagedGLAccountData.length; i++) {
          let row = this.pagedGLAccountData[i];
         if (row.isGLMainRow && !row.isHistorical && row.glAccountId == glAccountId) {
           break;
         }
       }
     
       return this.glAccountDataGridOptions.api.getRowNode(i);
      },
      getSummaryGLAccountRowNode(glAccountId) {
      let i = 0;
      for (i = 0; i < this.groupSummaryData.length; i++) {
        let row = this.groupSummaryData[i];
        if (!row.isHistorical && row.glAccountId == glAccountId) {
          break;
        }
      }
   
      return this.groupSummaryDataGridOptions.api.getRowNode(i);
     }
    },
    computed: {
      ...mapGetters(['selectedGroupName', 'currentLoading', 'isEditing']),
      currentHeaders() {
        return this.availableColumns;
      },
      summaryHeaders() {
        return this.summaryColumns;
      },
      totalsIcon() {
        return this.showTotals ? "mdi-arrow-right-drop-circle" : "mdi-arrow-left-drop-circle"
      }
    },
    beforeRouteLeave(to, from, next) {
      if (this.isEditing) {
        // pause navigation
        next(false);
        // save callback for after data has been saved
        this.pendingNavigationCallback = next;
      }
      else {
        next(true);
      }
    },
    async mounted() {
      window.addEventListener('scroll', this.handleScroll);
      window.addEventListener('paste', this.handlePaste);

      // get permissions/settings
      await this.loadUserSettings();

      // configure settings
      await this.loadBudgetSettings();

      // load budgets
      await this.loadBudgetGLAccounts();
    },
    unmounted() {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('paste', this.handlePaste);
      this.changeIsEditing(false);
    },
    created() {
      this.$eventHub.$on('budgetSettingsUpdated', this.loadBudgetSettings);
    },
    watch: {
      async $route(to, from) {
        let budgetChanged = to.params.budgetIterationId != from.params.budgetIterationId;
        let groupChanged = to.params.groupId != from.params.groupId;

        if (budgetChanged) {
          this.loadBudgetSettings();
        }

        if (budgetChanged || groupChanged) {
          this.showCalculations = false;
          this.showAssumptions = false;
          this.allCheckSelected = false;
          this.anyCheckSelected = false;
          this.groupData.name = '';
          this.groupData.totals = [];
          this.glAccountData = [];
          await this.loadBudgetGLAccounts();
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
  .center {
    height: 30em;
    top: 50%;
    text-align: center;
  }

    .center div[role="progressbar"] {
      top: 50%;
    }

  div[role="progressbar"] {
    margin-top: -4px;
  }

  .v-input--selection-controls::v-deep {
    margin: 0 !important;
    padding: 0 !important;
  }

  .grid-controls {
    width: 100%;
    height: 40px;
    margin-bottom: 14px;
    margin-top: -8px;
  }

  .grid-radio-controls {
    display: inline-block;
    border: solid 1px var(--v-resGrey-lighten3);
    padding: 6px 0 0 10px;
    height: 100%;
  }

  .add-columns-controls {
    display: inline-block;
    border: solid 1px var(--v-resGrey-lighten3);
    vertical-align: top;
    height: 100%;
  }

  .add-columns-label {
    vertical-align: top;
    display: inline-block;
    padding: 7px 8px 0 10px;
    height: 100%;
  }

  .add-columns-option::v-deep {
    display: inline-block;
    
    .v-label {
      font-size: 13px !important;
      color: var(--v-resGrey-darken2);
      font-weight: bold;
      padding-right: 14px;
      padding-top: 8px;
    }

    .v-input--selection-controls__input {
      margin-top: 0;
      padding-top: 8px;
    }

    .v-messages {
      display: none;
    }
  }

  .ag-cell {
    text-align: center;
  }

  .help-controls {
    float: right;
    border: solid 1px var(--v-resGrey-lighten3);
    padding: 7px 6px 6px 6px;
    cursor: pointer;
  }

  .help-label {
    padding-right: 6px;
  }

  @media (min-width: 1264px) {
    .container {
      max-width: 1285px;
    }
  }

  .future-historical-indicator {
    background-color: #e2e2e2;
    width: 100%;
    padding: 3px;
    padding-right: 10px;
    margin-top: -1px;
    position: relative;
    display: block;
    text-align: right;
  }
  .pagination-input {
    flex: none;
    padding: 0 !important;
    margin-bottom: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;

    ::v-deep .v-input__slot {
      min-height: 30px !important;
      display: flex;
      width: 30px;
      border-radius: 6px;
      border-width: 1px;
      text-align: left;
      padding: 0 !important;

      &:before {
        border-style: none;
      }
    }

    ::v-deep input {
      margin-top: 0px;
      font-size: 13px;
      text-align: center;
    }
  }

  .paginator {
    background-color: white;
    border: none;
    border-top: none;
    height: 70px;
    font-size: 12px;
    margin-left: 0px;
    margin-right: 0px;

    ::v-deep .v-pagination__navigation {
      height: 20px;
      width: 20px !important;
      min-width: 20px !important;
      box-shadow: none;
      margin-top: 0;
      margin-bottom: 0;

      &:focus {
        outline: none !important;
      }

      .v-icon {
        height: 20px;
        width: 20px !important;
        min-width: 20px !important;
        font-size: 14px;
        color: var(--v-resGrey-darken1)
      }
    }

    ::v-deep .v-pagination__item {
      height: 20px;
      min-width: 20px !important;
      box-shadow: none;
      border: 1px solid var(--v-resGrey-lighten3);
      font-size: 12px;
    }
  }
</style>
<style>
  .gl-view-container {
    width: 100%; 
    overflow-x: auto; 
    display: flex; 
    flex-direction: column; 
    /*height: 700px;*/
  }
  .gl-totals-view {
    position: sticky;
    top: 0;
    z-index: 1;
  }
  .gl-details-view {
    height: 100%;
    overflow-y: auto;
  }
  .gl-summary-grid .ag-header-container {
    margin-top: -29px;
  }
  .gl-summary-grid .summary-gl-title {
    border-bottom: 10px solid white !important;
    font-weight: 450;
  }
  .gl-summary-grid .ag-header-viewport {
      background-color: white;
  }
  .gl-summary-grid .ag-header {
      border-bottom-color: white !important;
  }

  .gl-title {
    font-family: Roboto, sans-serif;
    font-size: 12px !important;
    font-weight: 700;
    letter-spacing: 0.1px;
  }

  .gl-detail-grid .group-label {
    font-size: 14px !important;
    font-weight: bold;
    color: black !important;
  }

  .group-totals-table .ag-center-cols-clipper {
    min-height: unset !important;
  }
  .gl-summary-grid .ag-center-cols-viewport {
    margin-top: -61px;
    height: fit-content !important;
  }

  .gl-summary-grid .summary-header {
    border: 1px solid black !important;
    border-top: 1px white !important;
    text-align: center;
  }

  .gl-detail-grid .ag-body-viewport {
    border: 1px solid #f5f5f5 !important;
  }

  .gl-detail-grid .ag-cell-inline-editing {
    border: none !important;
    box-shadow: none !important;
    background-color: transparent !important;
    height: 26px !important;
    margin-top: 10px !important;
    padding-left:28px !important;
  }

  .gl-summary-grid *,
  .gl-summary-gl.grid .historic-cell .ag-cell-value {
    color: black !important;
    white-space: pre-wrap;
  }

  .gl-summary-grid .label-cell,
  .gl-summary-grid .summary-header .ag-header-cell-text {
    white-space: pre-wrap !important;
  }

  .gl-summary-grid .blank-header,
  .gl-summary-grid .ag-header-group-cell-no-group {
    background-color: white;
  }

  .gl-summary-gl-grid .left-border-cell,
  .gl-summary-grid .left-border-cell {
    border-left: 1px solid black !important;
  }

  .gl-summary-grid .summary-header .ag-header-group-cell-label {
    justify-content: center !important;
  }

  .to-actuals-header,
  .to-reforecast-header {
    background-color: #d9eaf0;
  }

  .to-actuals-cell,
  .to-reforecast-cell {
    background-color: #DFEAF0;
  }

  .to-prior-header,
  .calc-assumptions-header {
    background-color: #e8e8e8;
  }
  .to-prior-cell {
      background-color:#f2f2f2;
  }

  .assumption-cell,
  .calculation-cell {
    white-space: break-spaces !important;
    word-break: keep-all !important;
    font-size: 12px;
    line-height: 14px;
    font-weight: 450;
  }

  div[col-id="assumptionStatement"] {
    line-height: 14px !important;
  }

  div[col-id="calculationSentences"] {
    line-height: 14px !important;
  }

  .gl-data-cell {
    font-weight: 450;
  }
  .icon-locked {
    color: var(--v-resRed-darken2);
  }
</style>
